import { Box } from '@mui/material';
import React from 'react';

import { formatInET } from '../../utils/helper';
import { colors } from '../theme/theme';

import {
    AmortizationScheduleResponse,
    AmortizationUpcomingPaymentItem,
    ContractDateRangeItem,
    ContractDateRangeResponse,
    ContractDetails,
    ContractFinancialItem,
    ContractFinancialSummaryResponse,
    ContractMetricBulletItem,
    ContractMetricBulletResponse,
    ContractMetricListResponse,
    ContractMetricRow,
    ContractSnapshotItem,
    ContractSnapshotResponse,
    ContractSummary,
    GenericSection,
    LinkedAssetItem,
    LinkedAssetResponse,
    LooseRecord,
    NextPaymentDueItem,
    NextPaymentDueResponse,
    OpenInvoiceItem,
    PaymentBreakdownItem,
    PaymentItem,
    ReplacementRule,
    ScheduledPaymentItem,
    ScheduledPaymentsResponse,
    SingleContractMetricResponse,
    SupportRequestItem,
} from './IMessageBubbleInterface';

/**
 * Apply an ordered list of regex replacements, threading each result into the next — exactly
 * equivalent to chaining `.replace(...).replace(...)`. Keeping the rules as data (instead of a long
 * call chain) keeps the cosmetic text-normalizers readable and low-complexity while preserving the
 * original, order-sensitive behavior.
 */
export const applyReplacements = (input: string, rules: ReplacementRule[]): string =>
    rules.reduce((text, [pattern, replacement]) => text.replace(pattern, replacement), input);

/** Uppercase the first character, then run the ordered cosmetic replacements over the remainder. */
export const capitalizeWithReplacements = (value: string, rules: ReplacementRule[]): string => {
    const trimmed = (value || '').trim();
    if (!trimmed) {
        return trimmed;
    }
    return trimmed.charAt(0).toUpperCase() + applyReplacements(trimmed.slice(1), rules);
};

// FAQ-style answers come back as "<echoed question> |#|@| <actual answer>". Everything before
// (and including) the `|#|@|` marker is dropped so only the answer is shown. Returns the text
// unchanged when the marker isn't present.
const FAQ_HEADER_DELIMITER = '|#|@|';
export const stripFaqQuestionPrefix = (text: string): string => {
    const delimiterIndex = text.indexOf(FAQ_HEADER_DELIMITER);
    if (delimiterIndex === -1) {
        return text;
    }
    return text.slice(delimiterIndex + FAQ_HEADER_DELIMITER.length).trim();
};

export const extractPayloadText = (rawText: string) => {
    const trimmedText = rawText.trim();
    if (!trimmedText.startsWith('{') && !trimmedText.startsWith('[')) {
        return stripFaqQuestionPrefix(rawText);
    }

    try {
        const parsed = JSON.parse(trimmedText);
        if (typeof parsed === 'string') {
            return stripFaqQuestionPrefix(parsed);
        }
        if (typeof parsed?.data === 'string') {
            return stripFaqQuestionPrefix(parsed.data);
        }
        if (typeof parsed?.message === 'string') {
            return stripFaqQuestionPrefix(parsed.message);
        }
    } catch {
        return stripFaqQuestionPrefix(rawText);
    }

    return stripFaqQuestionPrefix(rawText);
};

// Pull the envelope's header `message` when the payload is JSON whose `data` is a delimited STRING
// (e.g. { message: "Here are your payment details:", data: "Contract ID: ... | ..." }). Returns the
// header so list cards can show it as the title instead of echoing the first data row. Empty string
// when there's no such header (plain string payloads, or `data` isn't a string).
export const extractEnvelopeMessage = (rawText: string): string => {
    const trimmedText = rawText.trim();
    if (!trimmedText.startsWith('{')) {
        return '';
    }
    try {
        const parsed = JSON.parse(trimmedText);
        if (typeof parsed?.data === 'string' && typeof parsed?.message === 'string') {
            return parsed.message.trim();
        }
    } catch {
        return '';
    }
    return '';
};

// Shared unexported variant of extractPayloadText above: it returns the raw envelope field
// (rawText/parsed.data/parsed.message) unwrapped, without stripping the FAQ header via
// stripFaqQuestionPrefix. Several parsers below used to redefine this identically inline; they
// now call this shared copy instead. When `preserveArrayData` is true, an array `data` field
// causes the raw text to be returned unchanged (needed by parsePaymentHistory).
const extractRawPayloadText = (rawText: string, preserveArrayData = false): string => {
    const trimmedText = rawText.trim();
    if (!trimmedText.startsWith('{') && !trimmedText.startsWith('[')) {
        return rawText;
    }

    try {
        const parsed = JSON.parse(trimmedText);
        if (typeof parsed?.data === 'string') {
            return parsed.data;
        }
        if (preserveArrayData && Array.isArray(parsed?.data)) {
            return rawText;
        }
        if (typeof parsed?.message === 'string') {
            return parsed.message;
        }
    } catch {
        return rawText;
    }

    return rawText;
};

// Escape a label so it can be interpolated into a `new RegExp(...)` source string as a literal
// match. Shared by the several pipe-delimited "Label: value" field parsers below — each builds
// its own value-side lookahead, but they all escape the label the same way.
const escapeRegexLabel = (label: string): string => label.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

// Shared by parseStructuredPaymentHistory and parsePaymentHistory: de-dupe a list of values
// (trimming and dropping blanks) and join them with " | "; empty result renders as "-".
const joinUniqueValues = (values: string[]): string => {
    const uniqueValues = Array.from(new Set(values.map(value => value.trim()).filter(Boolean)));
    return uniqueValues.length ? uniqueValues.join(' | ') : '-';
};

// Shared by parseContracts and parseNextPaymentDue: pulls `Label: value` out of a
// pipe-delimited row, where the value runs until the next bounded `Label:` pair or end of
// string. Label runs are bounded with `{0,80}` (a label is never longer than that) so the
// greedy class can't be re-scanned across a long input from every start position — that
// unbounded re-scan is what causes super-linear backtracking. Real labels fit well within 80
// chars, so matching behavior is unchanged.
const parseFieldByLabel = (source: string, label: string): string => {
    const escapedLabel = escapeRegexLabel(label);
    const regex = new RegExp(`${escapedLabel}\\s*:\\s*([^|]+?)(?=\\s*\\|\\s*[A-Za-z][A-Za-z ]{0,80}:|\\s*$)`, 'i');
    return source.match(regex)?.[1]?.trim() || '';
};

// Shared by the two contract-row mappers inside parseContracts (numbered-line rows and
// blank-line-delimited blocks): derive a "N of M payments"/"K remaining" progress string from
// whichever of total/done/remaining counts are available.
const computeDerivedProgress = (
    totalPayments: number | null,
    paymentsDone: number | null,
    remainingPayments: number | null,
): string => {
    if (totalPayments !== null && paymentsDone !== null && totalPayments >= paymentsDone) {
        return `${paymentsDone} of ${totalPayments} payments`;
    }
    if (totalPayments !== null && remainingPayments !== null && totalPayments >= remainingPayments) {
        return `${totalPayments - remainingPayments} of ${totalPayments} payments`;
    }
    if (remainingPayments !== null) {
        return `${remainingPayments} remaining`;
    }
    return '';
};

// Alias-matching helpers shared by ContractMetricListCard and GenericBotResponseCard for looking
// up a metric/key-value entry by one of several possible label spellings (e.g. "Serial Number",
// "Serial Number (VIN)", "VIN"). Extracted verbatim from both call sites — behavior unchanged.
const normalizeMetricLabelForAliasMatch = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');
const tokenizeMetricLabelForAliasMatch = (value: string) =>
    value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .split(/\s+/)
        .filter(Boolean);

export const matchesMetricAlias = (metricLabel: string, aliases: string[]): boolean => {
    const normalizedMetric = normalizeMetricLabelForAliasMatch(metricLabel);
    const metricTokens = tokenizeMetricLabelForAliasMatch(metricLabel);

    return aliases.some(alias => {
        const normalizedAlias = normalizeMetricLabelForAliasMatch(alias);
        const aliasTokens = tokenizeMetricLabelForAliasMatch(alias);

        if (!normalizedAlias) {
            return false;
        }

        if (
            normalizedMetric === normalizedAlias ||
            normalizedMetric.includes(normalizedAlias) ||
            normalizedAlias.includes(normalizedMetric)
        ) {
            return true;
        }

        return aliasTokens.length > 0 && aliasTokens.every(token => metricTokens.includes(token));
    });
};

/** Finds the trimmed value of the first entry whose label matches any of `aliases`. */
export const findValueByAliases = (entries: Array<{ label: string; value: string }>, aliases: string[]): string => {
    const entry = entries.find(({ label }) => matchesMetricAlias((label || '').trim(), aliases));
    return (entry?.value || '').trim();
};

export const formatTime = (date: string | Date) => {
    return `${formatInET(date, { hour: '2-digit', minute: '2-digit', hour12: true })} ET`;
};

export const parseAmount = (value: string) => {
    if (!value) {
        return 0;
    }
    return parseFloat(value.replace(/[^0-9.]/g, '')) || 0;
};

export const formatCurrency = (value: string) => {
    // Amounts are always shown as positive — strip any sign so figures like
    // "-48.78" (year-to-date interest/principal) render as "$48.78" in the UI.
    const amount = Math.abs(parseAmount(value));
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Single source of truth for date display in the chatbot: always MM/DD/YYYY (zero-padded).
// Handles ISO timestamps ("2026-02-27T15:26:50.000Z"), MM-DD-YYYY, and M/D/YYYY inputs.
// Returns the original value untouched when it isn't a parseable date.
export const formatDate = (value: unknown) => {
    if (value === null || value === undefined) {
        return '';
    }
    const raw = String(value).trim();
    if (!raw) {
        return '';
    }

    // Already-delimited date (MM-DD-YYYY, M/D/YYYY, etc.): pad and normalize separators
    // without going through Date(), which would apply a timezone shift to date-only values.
    const delimited = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (delimited) {
        const [, month, day, year] = delimited;
        return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
    }

    // ISO date-only ("2026-02-27"): format from the parts so it isn't shifted across
    // the date line by the local timezone.
    const isoDateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoDateOnly) {
        const [, year, month, day] = isoDateOnly;
        return `${month}/${day}/${year}`;
    }

    // ISO timestamp ("2026-02-27T15:26:50.000Z") or any other Date-parseable string: use the
    // UTC parts so the calendar date the API sent is preserved regardless of local timezone.
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) {
        return raw;
    }
    const month = String(parsed.getUTCMonth() + 1).padStart(2, '0');
    const day = String(parsed.getUTCDate()).padStart(2, '0');
    const year = parsed.getUTCFullYear();
    return `${month}/${day}/${year}`;
};

export const extractFollowUpQuestion = (sourceLines: string[]): string | null => {
    const lines = sourceLines.map(line => line.trim()).filter(Boolean);
    if (!lines.length) {
        return null;
    }

    const questionLike = lines.find(
        line => line.includes('?') || /^(would|could|can|do|are|is|should|want|let me know|please)/i.test(line),
    );

    if (questionLike) {
        return questionLike;
    }

    const isLabeledLine = (line: string): boolean => {
        const colonIndex = line.indexOf(':');
        if (colonIndex < 1 || colonIndex > 60) {
            return false;
        }
        return /\S/.test(line.slice(colonIndex + 1));
    };

    const fallback = lines.find(line => !/^\d+\./.test(line) && !isLabeledLine(line));

    return fallback || null;
};

const matchFirstGroup = (text: string, regex: RegExp) => text.match(regex)?.[1]?.trim() || '';

const extractNarrativeInvoiceAmount = (narrativeText: string) =>
    matchFirstGroup(narrativeText, /past\s+due\s+amount\s+of\s*(\$?[\d,]+(?:\.\d{1,2})?)/i) ||
    matchFirstGroup(narrativeText, /amount\s+of\s*(\$?[\d,]+(?:\.\d{1,2})?)/i) ||
    matchFirstGroup(narrativeText, /amount\s*(?:due\s*)?:\s*(\$?[\d,]+(?:\.\d{1,2})?)/i);

const extractNarrativeInvoiceNumber = (narrativeText: string) =>
    matchFirstGroup(narrativeText, /invoice\s*(?:number|#)\s*(?:[:#]\s*)?([A-Za-z0-9-]+)/i) ||
    matchFirstGroup(narrativeText, /from\s+invoice\s*(?:[:#]\s*)?([A-Za-z0-9-]+)/i);

const extractNarrativeContractId = (narrativeText: string) =>
    matchFirstGroup(narrativeText, /contract\s*(?:id|#)\s*(?:[:#]\s*)?([A-Za-z0-9-]+)/i) ||
    matchFirstGroup(narrativeText, /contract\s+([A-Za-z0-9-]+)/i);

const extractNarrativeRemainingDue = (narrativeText: string) =>
    matchFirstGroup(narrativeText, /remaining\s+due(?:\s+amount)?\s+of\s*(\$?[\d,]+(?:\.\d{1,2})?)/i) ||
    matchFirstGroup(narrativeText, /remaining\s+due\s*:\s*(\$?[\d,]+(?:\.\d{1,2})?)/i);

const extractNarrativeLateFee = (narrativeText: string) =>
    matchFirstGroup(narrativeText, /late\s+fee\s+of\s*(\$?[\d,]+(?:\.\d{1,2})?)/i) ||
    matchFirstGroup(narrativeText, /late\s+fee\s*:\s*(\$?[\d,]+(?:\.\d{1,2})?)/i);

const extractNarrativeDueDate = (narrativeText: string) =>
    matchFirstGroup(narrativeText, /invoice\s+dated\s*([0-9]{1,2}[/-][0-9]{1,2}[/-][0-9]{2,4})/i);

// Single-invoice narrative fallback for parseInvoices, hoisted to component scope so its branching
// isn't counted against the parent. Behavior is unchanged — the block is moved verbatim, with the
// values it referenced from the parent (allLines + the three summary fields) passed in as params.
export const buildSingleInvoiceFromNarrative = (
    allLines: string[],
    summaryNextDueDate: string,
    summaryPastDueAmount: string,
    summaryRemainingPrincipalAmount: string,
): { intro: string; items: OpenInvoiceItem[]; followUp: string | null } | null => {
    const narrativeText = allLines.join(' ');

    const amount = extractNarrativeInvoiceAmount(narrativeText);
    const dueDate = extractNarrativeDueDate(narrativeText);
    const invoiceNumber = extractNarrativeInvoiceNumber(narrativeText);
    const contractId = extractNarrativeContractId(narrativeText);
    const remainingDue = extractNarrativeRemainingDue(narrativeText);
    const lateFee = extractNarrativeLateFee(narrativeText);
    const overdue = /\bis\s+overdue\b/i.test(narrativeText);

    if (!amount && !dueDate && !invoiceNumber) {
        return null;
    }

    const singleItem: OpenInvoiceItem = {
        contractId: contractId || '-',
        dueDate: dueDate || '-',
        nextDueDate: summaryNextDueDate || '',
        invoiceNumber: invoiceNumber || '-',
        amountDue: amount || '-',
        remainingDue: remainingDue || amount || '-',
        pastDueAmount: summaryPastDueAmount || '',
        remainingPrincipalAmount: summaryRemainingPrincipalAmount || '',
        autopayEnabled: 'No',
        action: '',
        overdue,
        originalDueAmount: amount || '-',
        segregationDescription: '',
        segregationAmount: '',
        totalPendingScheduleAmount: '',
        invoiceDetailNumber: '',
        isActive: '',
        lateFee: lateFee || '',
    };

    const followUpQuestion = extractFollowUpQuestion(allLines);
    const guidanceLines = allLines.filter((line: string) => line !== followUpQuestion);
    const introCandidate = guidanceLines[0] || 'Open invoices';
    // Each whitespace run bounded so a pathological run of spaces can't force unbounded regex work.
    const intro =
        (introCandidate.split(/\s{1,10}for\s{1,10}complete\s{1,10}open\s{1,10}invoices/i)[0] || '').trim() ||
        'Open invoices';
    const followUpContentLines = allLines.filter((line: string) => line !== introCandidate);
    const followUp = followUpContentLines.length > 0 ? followUpContentLines.join('\n') : followUpQuestion || null;

    return {
        intro,
        items: [singleItem],
        followUp,
    };
};

const normalizeInvoiceFieldKey = (key: string) => key.toLowerCase().replace(/[^a-z0-9]/g, '');

// Parses all `Label: value` pairs from one invoice line (pipe- or comma-separated).
// Label runs are bounded with `{0,80}` (a label is never longer than that) so the greedy
// class can't be re-scanned across a long input from every start position — that unbounded
// re-scan is what causes super-linear backtracking. Real labels fit well within 80 chars, so
// matching behavior is unchanged.
const parseInvoiceLineFields = (source: string): Record<string, string> => {
    const isPipe = source.includes(' | ');
    const pairRegex = isPipe
        ? /([A-Za-z][A-Za-z ]{0,80}):\s{0,20}([^|]+?)(?=\s{0,20}\|\s{0,20}[A-Za-z][A-Za-z ]{0,80}:|$)/g
        : /([A-Za-z][A-Za-z ]{0,80}):\s{0,20}([^,]+?)(?=,\s{0,20}[A-Za-z][A-Za-z ]{0,80}:|$)/g;

    const fields: Record<string, string> = {};
    let match = pairRegex.exec(source);
    while (match) {
        fields[normalizeInvoiceFieldKey(match[1].trim())] = match[2].trim();
        match = pairRegex.exec(source);
    }
    return fields;
};

const getInvoiceFieldValue = (fields: Record<string, string>, ...keys: string[]): string =>
    keys.map(key => fields[normalizeInvoiceFieldKey(key)]).find(value => value != null && value !== '') || '';

const computeInvoiceLateFee = (
    directLateFee: string,
    segregationDescription: string,
    segregationAmount: string,
): string => {
    if (directLateFee) {
        return directLateFee;
    }
    return /late\s*fee/i.test(segregationDescription) ? segregationAmount : '';
};

// Remaining due: prioritize the API-provided value; otherwise fall back to amount minus total
// scheduled (backwards compatibility with the legacy payload shape).
const computeInvoiceRemainingDue = (amount: string, totalPending: string, apiRemainingDue: string): string => {
    if (apiRemainingDue) {
        return apiRemainingDue;
    }
    const amountNum = parseFloat(amount.replace(/[^0-9.]/g, '')) || 0;
    const pendingNum = parseFloat(totalPending.replace(/[^0-9.]/g, '')) || 0;
    if (pendingNum <= 0) {
        return amount;
    }
    const remainingNum = Math.max(0, amountNum - pendingNum);
    return remainingNum === 0 ? '0.00' : remainingNum.toFixed(2);
};

const computeInvoiceOverdueFlag = (overdueRaw: string, source: string): boolean => {
    const normalizedOverdue = overdueRaw.toLowerCase().trim();
    if (['true', 'yes', 'y', '1'].includes(normalizedOverdue)) {
        return true;
    }
    const lowerSource = source.toLowerCase();
    return lowerSource.includes('is overdue: true') || lowerSource.includes('is overdue: yes');
};

// Builds one OpenInvoiceItem from a single source line. Hoisted out of parseInvoices' map
// callback so its branching isn't counted against that callback's own complexity; behavior is
// unchanged — same field parsing/fallback rules, just split into named steps.
const buildOpenInvoiceItem = (
    line: string,
    summaryContractId: string,
    summaryNextDueDate: string,
    summaryPastDueAmount: string,
    summaryRemainingPrincipalAmount: string,
): OpenInvoiceItem => {
    const source = line.replace(/^\d+\.\s*/, '').trim();
    const fields = parseInvoiceLineFields(source);
    const get = (...keys: string[]) => getInvoiceFieldValue(fields, ...keys);

    const amount = get('amount', 'amount due');
    const totalPending = get('total pending schedule amount');
    const originalDue = get('original due amount') || amount;
    const segregationDescription = get('segregation description');
    const segregationAmount = get('segregation amount');
    const lateFee = computeInvoiceLateFee(get('late fee'), segregationDescription, segregationAmount);
    const remainingDue = computeInvoiceRemainingDue(amount, totalPending, get('remaining due'));
    const overdue = computeInvoiceOverdueFlag(get('is overdue', 'overdue'), source);

    const contractId = get('contract id', 'contractid') || summaryContractId;
    const summaryAppliesToInvoice =
        !summaryContractId || !contractId || summaryContractId.toLowerCase() === contractId.toLowerCase();

    return {
        contractId,
        dueDate: get('due date'),
        nextDueDate: summaryAppliesToInvoice ? summaryNextDueDate : '',
        invoiceNumber: get('invoice number'),
        amountDue: amount || '-',
        remainingDue,
        pastDueAmount: summaryAppliesToInvoice ? summaryPastDueAmount : '',
        remainingPrincipalAmount: summaryAppliesToInvoice ? summaryRemainingPrincipalAmount : '',
        autopayEnabled: get('autopay enabled', 'autopay') || 'No',
        action: '',
        overdue,
        originalDueAmount: originalDue,
        segregationDescription,
        segregationAmount,
        totalPendingScheduleAmount: totalPending,
        invoiceDetailNumber: get('invoice detail number'),
        isActive: get('is active'),
        lateFee,
    } as OpenInvoiceItem;
};

// Structured JSON path: the API now returns open invoices as an array. Map it directly
// to the OpenInvoiceItem shape the renderer consumes, bypassing all text parsing.
// Hoisted to component scope (out of parseInvoices) so its branching isn't counted against the
// parent's complexity; behavior is unchanged (same closure over formatDate).
export const parseStructuredInvoices = (rawText: string) => {
    const trimmedText = rawText.trim();
    if (!trimmedText.startsWith('{') && !trimmedText.startsWith('[')) {
        return null;
    }

    const formatInvoiceDate = (value: unknown) => {
        if (value === null || value === undefined || value === '') {
            return '-';
        }
        return formatDate(value);
    };

    const toDisplay = (value: unknown) => {
        if (value === null || value === undefined || value === '') {
            return '';
        }
        return String(value);
    };

    try {
        let parsedPayload: LooseRecord = JSON.parse(trimmedText);
        if (typeof parsedPayload?.data === 'string') {
            const nested = parsedPayload.data.trim();
            if (nested.startsWith('{') || nested.startsWith('[')) {
                parsedPayload = JSON.parse(nested);
            }
        }

        const rows: LooseRecord[] | null = Array.isArray(parsedPayload?.data) ? parsedPayload.data : null;
        if (!rows || !rows.length) {
            return null;
        }

        const looksLikeInvoices = rows.some(
            (row: LooseRecord) => row && (row.invoice_number !== undefined || row.due_date !== undefined),
        );
        if (!looksLikeInvoices) {
            return null;
        }

        const items: OpenInvoiceItem[] = rows
            .map((row: LooseRecord): OpenInvoiceItem | null => {
                const invoiceNumber = toDisplay(row?.invoice_number);
                if (!invoiceNumber) {
                    return null;
                }

                const isOverdue = row?.is_overdue === true || /^(true|yes|y|1)$/i.test(toDisplay(row?.is_overdue));

                return {
                    contractId: toDisplay(row?.contract_id) || '-',
                    dueDate: formatInvoiceDate(row?.due_date),
                    nextDueDate: '',
                    invoiceNumber,
                    amountDue: toDisplay(row?.amount) || '-',
                    remainingDue: toDisplay(row?.remaining_due ?? row?.amount) || '-',
                    pastDueAmount: '',
                    remainingPrincipalAmount: '',
                    autopayEnabled: 'No',
                    action: '',
                    overdue: isOverdue,
                    originalDueAmount: toDisplay(row?.original_due_amount),
                    segregationDescription: toDisplay(row?.segregation_description),
                    segregationAmount: toDisplay(row?.segregation_amount),
                    totalPendingScheduleAmount: toDisplay(row?.total_pending_schedule_amount),
                    invoiceDetailNumber: toDisplay(row?.invoice_detail_number),
                    isActive: toDisplay(row?.is_active),
                    lateFee: toDisplay(row?.late_fee),
                };
            })
            .filter((item: OpenInvoiceItem | null): item is OpenInvoiceItem => Boolean(item));

        if (!items.length) {
            return null;
        }

        return {
            intro: typeof parsedPayload?.message === 'string' ? parsedPayload.message : 'Open invoices',
            items,
            followUp: typeof parsedPayload?.follow_up === 'string' ? parsedPayload.follow_up : null,
        };
    } catch {
        return null;
    }
};

const hasOpenInvoiceListHeader = (payloadText: string): boolean =>
    /here are\s+(?:(?:all\s+)?(?:your|the)\s+)?open invoices/i.test(payloadText) ||
    /past due amounts?\s+on\s+several\s+invoices/i.test(payloadText) ||
    /here\s+is\s+the\s+detailed\s+information\s+for\s+invoice\s+number/i.test(payloadText) ||
    /some examples include/i.test(payloadText);

const hasSingleInvoiceNarrativeHeader = (payloadText: string): boolean =>
    (/past\s+due\s+amount/i.test(payloadText) && /(invoice\s+dated|invoice\s*#|invoice\s+number)/i.test(payloadText)) ||
    (/(invoice\s+number|invoice\s*#)/i.test(payloadText) &&
        /contract\s+[A-Za-z0-9-]+/i.test(payloadText) &&
        /(amount\s+of|remaining\s+due|late\s+fee|is\s+overdue)/i.test(payloadText));

const extractSummaryField = (allLines: string[], labelPattern: RegExp): string =>
    allLines
        .find((line: string) => labelPattern.test(line))
        ?.replace(labelPattern, '')
        .trim() || '';

const pickInvoiceSourceLines = (
    numberedLines: string[],
    inlineInvoiceDetailLines: string[],
    fallbackLines: string[],
): string[] => {
    if (numberedLines.length) return numberedLines;
    if (inlineInvoiceDetailLines.length) return inlineInvoiceDetailLines;
    return fallbackLines;
};

const extractInvoiceListIntro = (payloadText: string): string =>
    payloadText
        .split('\n')
        .map((line: string) => line.trim())
        .find((line: string) => Boolean(line) && !/^\d+\./.test(line) && !/^would\s+you\s+like/i.test(line)) ||
    'Open invoices';

// Follow-up text for an invoice list is whatever trails the last numbered invoice entry.
const extractInvoiceListFollowUp = (payloadText: string): string | null => {
    const rawLines = payloadText.split('\n');
    const lastNumberedIdx = rawLines.reduce(
        (last: number, line: string, idx: number) =>
            line.match(/^\d+\./) && /Due Date:/i.test(line) && /Invoice Number:/i.test(line) ? idx : last,
        -1,
    );
    if (lastNumberedIdx < 0) {
        return null;
    }

    const followUpLines = rawLines
        .slice(lastNumberedIdx + 1)
        .map((line: string) => line.trim())
        .filter((line: string) => line.length > 0);
    return followUpLines.length > 0 ? followUpLines.join('\n') : null;
};

// Helper: Detect and parse invoice list from bot message
export const parseInvoices = (
    text: string,
): { intro: string; items: OpenInvoiceItem[]; followUp: string | null } | null => {
    const structuredInvoices = parseStructuredInvoices(text);
    if (structuredInvoices) {
        return structuredInvoices;
    }

    const payloadText = extractPayloadText(text);
    const allLines = payloadText
        .split('\n')
        .map((line: string) => line.trim())
        .filter((line: string) => line.length > 0);

    // Match invoice headers: "Here are your open invoices:", "Here are the open invoices for contract...", etc.
    const hasHeader = hasOpenInvoiceListHeader(payloadText);
    const hasSingleInvoiceNarrative = hasSingleInvoiceNarrativeHeader(payloadText);

    // Support both pipe-separated (new API) and comma-separated (legacy) formats
    // Only match numbered lines that look like actual invoices (contain invoice-specific fields)
    const numberedLines = payloadText
        .split('\n')
        .filter((line: string) => line.match(/^\d+\./) && /Due Date:/i.test(line) && /Invoice Number:/i.test(line));
    const inlineInvoiceDetailLines = payloadText
        .split('\n')
        .map((line: string) => line.trim())
        .filter(
            (line: string) =>
                !/^\d+\./.test(line) && /Due Date:/i.test(line) && /Invoice Number:/i.test(line) && /\|/.test(line),
        );
    const looksLikeInvoiceList = numberedLines.length > 0 || inlineInvoiceDetailLines.length > 0;

    if (!hasHeader && !looksLikeInvoiceList && !hasSingleInvoiceNarrative) {
        return null;
    }

    const fallbackLines = payloadText.split('\n').filter((line: string) => line.match(/Contract ID/));
    const sourceLines = pickInvoiceSourceLines(numberedLines, inlineInvoiceDetailLines, fallbackLines);

    const summaryContractIdMatch = payloadText.match(/(?:for|on)\s+contract\s+([A-Za-z0-9-]+)/i);
    const summaryContractId = summaryContractIdMatch?.[1]?.trim() || '';

    const summaryPastDueAmount = extractSummaryField(allLines, /^-\s*past\s+due\s+amount\s*:/i);
    const summaryNextDueDate = extractSummaryField(allLines, /^-\s*next\s+due\s+date\s*:/i);
    const summaryRemainingPrincipalAmount = extractSummaryField(allLines, /^-\s*remaining\s+principal\s+amount\s*:/i);

    if (!sourceLines.length && hasSingleInvoiceNarrative) {
        return buildSingleInvoiceFromNarrative(
            allLines,
            summaryNextDueDate,
            summaryPastDueAmount,
            summaryRemainingPrincipalAmount,
        );
    }

    if (!sourceLines.length) {
        return null;
    }

    const intro = extractInvoiceListIntro(payloadText);

    // Extract follow-up text: lines after all numbered invoice entries
    const followUp = extractInvoiceListFollowUp(payloadText);
    const followUpFromQuestion = extractFollowUpQuestion(
        allLines.filter((line: string) => !sourceLines.includes(line)),
    );

    const items = sourceLines.map((line: string) =>
        buildOpenInvoiceItem(
            line,
            summaryContractId,
            summaryNextDueDate,
            summaryPastDueAmount,
            summaryRemainingPrincipalAmount,
        ),
    );

    return { intro, items, followUp: followUp || followUpFromQuestion };
};

// Helper: Detect and parse scheduled payments from bot message
export const parseScheduledPayments = (
    text: string,
    isScheduledPaymentsQuery: boolean,
): ScheduledPaymentsResponse | null => {
    const payloadText = extractPayloadText(text);

    const parseStructuredScheduledPayments = (rawText: string): ScheduledPaymentsResponse | null => {
        const trimmedText = rawText.trim();
        if (!trimmedText.startsWith('{') && !trimmedText.startsWith('[')) {
            return null;
        }

        const toDisplayValue = (value: unknown) => {
            if (value === null || value === undefined || value === '') {
                return '-';
            }
            return String(value);
        };

        const parseDateKey = (value: string) => {
            if (!value || value === '-') {
                return 0;
            }

            const normalized = value.replace(/\//g, '-');
            const mmddyyyy = normalized.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
            if (mmddyyyy) {
                const month = mmddyyyy[1].padStart(2, '0');
                const day = mmddyyyy[2].padStart(2, '0');
                return new Date(`${mmddyyyy[3]}-${month}-${day}T00:00:00Z`).getTime();
            }

            const parsed = new Date(value).getTime();
            return Number.isNaN(parsed) ? 0 : parsed;
        };

        try {
            let parsedPayload: LooseRecord = JSON.parse(trimmedText);
            if (typeof parsedPayload?.data === 'string') {
                const nestedData = parsedPayload.data.trim();
                if (nestedData.startsWith('{') || nestedData.startsWith('[')) {
                    parsedPayload = { ...parsedPayload, data: JSON.parse(nestedData) };
                }
            }

            const dataBlock = parsedPayload?.data;

            if (Array.isArray(dataBlock)) {
                const responseMessage = typeof parsedPayload?.message === 'string' ? parsedPayload.message : '';
                const messageMentionsScheduled = /schedul/i.test(responseMessage);
                if (!isScheduledPaymentsQuery && !messageMentionsScheduled) {
                    return null;
                }

                const flatPayments: ScheduledPaymentItem[] = (dataBlock as LooseRecord[])
                    .filter((row: LooseRecord) => row && typeof row === 'object')
                    .map((row: LooseRecord) => ({
                        contractId: toDisplayValue(row.contract_id ?? row.ContractId),
                        scheduledDate: toDisplayValue(row.scheduled_date ?? row.ScheduledDate),
                        amount: toDisplayValue(row.amount_due ?? row.AmountDue),
                        status: toDisplayValue(row.status ?? row.Status ?? 'Pending'),
                        recordId: toDisplayValue(row.record_id ?? row.RecordId),
                        paymentOId: toDisplayValue(row.payment_oid ?? row.PaymentOId),
                        aspireContractId: toDisplayValue(row.aspire_contract_id ?? row.AspireContractId),
                        createdTime: toDisplayValue(row.created_time ?? row.CreatedTime),
                    }));

                if (!flatPayments.length) {
                    return null;
                }

                const sortedFlatPayments = [...flatPayments].sort(
                    (a, b) => parseDateKey(b.scheduledDate) - parseDateKey(a.scheduledDate),
                );

                const flatFollowUp = String(parsedPayload?.follow_up || '').trim() || null;
                const flatIntro =
                    typeof parsedPayload?.message === 'string' && parsedPayload.message.trim()
                        ? parsedPayload.message.trim()
                        : null;

                return {
                    payments: sortedFlatPayments,
                    followUp: flatFollowUp,
                    intro: flatIntro,
                };
            }

            if (!dataBlock || typeof dataBlock !== 'object') {
                return null;
            }

            const payments: ScheduledPaymentItem[] = [];

            Object.entries(dataBlock).forEach(([contractKey, entries]) => {
                if (!Array.isArray(entries)) {
                    return;
                }

                (entries as LooseRecord[]).forEach((entry: LooseRecord) => {
                    if (!entry || typeof entry !== 'object') {
                        return;
                    }

                    const hasScheduledShape =
                        entry.ScheduledDate != null ||
                        entry.AmountDue != null ||
                        entry.Status != null ||
                        entry.RecordId != null ||
                        entry.PaymentOId != null;

                    if (!hasScheduledShape) {
                        return;
                    }

                    payments.push({
                        contractId: toDisplayValue(entry.ContractId || contractKey),
                        scheduledDate: toDisplayValue(entry.ScheduledDate),
                        amount: toDisplayValue(entry.AmountDue),
                        status: toDisplayValue(entry.Status || 'Pending'),
                        recordId: toDisplayValue(entry.RecordId),
                        paymentOId: toDisplayValue(entry.PaymentOId),
                        aspireContractId: toDisplayValue(entry.AspireContractId),
                        createdTime: toDisplayValue(entry.CreatedTime),
                    });
                });
            });

            if (!payments.length) {
                return null;
            }

            const sortedPayments = [...payments].sort((a, b) => {
                const dateDiff = parseDateKey(b.scheduledDate) - parseDateKey(a.scheduledDate);
                if (dateDiff !== 0) {
                    return dateDiff;
                }
                return parseDateKey(b.createdTime) - parseDateKey(a.createdTime);
            });

            const followUpRaw = String(parsedPayload?.follow_up || '').trim();
            const followUp = followUpRaw || null;

            return {
                payments: sortedPayments,
                followUp,
            };
        } catch {
            return null;
        }
    };

    const structuredScheduledPayments = parseStructuredScheduledPayments(text);
    if (structuredScheduledPayments) {
        return structuredScheduledPayments;
    }

    const structuredScheduledPaymentsFromPayloadText = parseStructuredScheduledPayments(payloadText);
    if (structuredScheduledPaymentsFromPayloadText) {
        return structuredScheduledPaymentsFromPayloadText;
    }

    const hasActualScheduledDate =
        /scheduled\s+(?:payment\s+)?date\s*:\s*\d/i.test(payloadText) ||
        /scheduled\s+date\s*:\s*\d/i.test(payloadText) ||
        /schedule\s+date\s*:\s*\d/i.test(payloadText) ||
        /payment\s+date\s*:\s*\d/i.test(payloadText);

    const looksLikeScheduledPaymentList =
        payloadText.toLowerCase().includes('scheduled payment') &&
        hasActualScheduledDate &&
        /(contract\s*id|contractid)\s*:/i.test(payloadText);

    const looksLikeNarrativeSingleScheduledPayment =
        /scheduled\s+payment/i.test(payloadText) &&
        /(?:set\s+for|scheduled\s+for|on)\s*[0-9]{1,2}[/-][0-9]{1,2}[/-][0-9]{2,4}/i.test(payloadText) &&
        /(?:amount\s+due\s+of|amount\s+due\s+is|amount\s+of|for)\s*(?:\$\s*)?[\d,]+(?:\.\d{1,2})?/i.test(payloadText);

    if (!looksLikeScheduledPaymentList && !looksLikeNarrativeSingleScheduledPayment) {
        return null;
    }

    const allLines = payloadText
        .split('\n')
        .map((line: string) => line.trim())
        .filter(Boolean);

    const paymentLineIndexes = allLines
        .map((line: string, index: number) => {
            const looksLikePaymentRow =
                /(contract\s*id|contractid)\s*:/i.test(line) &&
                /(scheduled\s+date|schedule\s+date|payment\s+date)\s*:/i.test(line) &&
                /(amount\s+due|amount)\s*:/i.test(line);

            return looksLikePaymentRow ? index : -1;
        })
        .filter((index: number) => index >= 0);

    if (!paymentLineIndexes.length) {
        const narrativeMatch = payloadText.match(
            /scheduled\s+payment[\s\S]*?(?:set\s+for|scheduled\s+for|on)\s*([0-9]{1,2}[/-][0-9]{1,2}[/-][0-9]{2,4})[\s\S]*?(?:amount\s+due\s+(?:of|is)|amount\s+of|for)\s*(?:\$\s*)?([\d,]+(?:\.\d{1,2})?)/i,
        );

        if (!narrativeMatch) {
            return null;
        }

        const contractMatch = payloadText.match(/contract\s+id\s*:\s*([^\n|]+)/i);
        const status = /cancelled|canceled/i.test(payloadText)
            ? 'Cancelled'
            : /pending/i.test(payloadText)
              ? 'Pending'
              : /processed|completed|success/i.test(payloadText)
                ? 'Processed'
                : 'Pending';
        const noActiveIntroMatch = payloadText.match(/You currently have no active scheduled payments\./i);
        const intro = noActiveIntroMatch?.[0]?.trim() || null;

        const _navIdx = payloadText.search(/For complete Scheduled Payment/i);
        const navigationBlockMatch = _navIdx >= 0 ? [payloadText.slice(_navIdx)] : null;
        const questionLineMatch = payloadText.match(/Would you like[^\n?]*\?/i);
        const followUpParts = [navigationBlockMatch?.[0]?.trim() || '', questionLineMatch?.[0]?.trim() || ''].filter(
            Boolean,
        );

        return {
            payments: [
                {
                    contractId: contractMatch?.[1]?.trim() || '-',
                    scheduledDate: narrativeMatch[1].trim(),
                    amount: narrativeMatch[2].trim(),
                    status,
                    recordId: '-',
                    paymentOId: '-',
                    aspireContractId: '-',
                    createdTime: '-',
                },
            ],
            intro,
            followUp: followUpParts.length ? followUpParts.join('\n') : null,
        };
    }

    const paymentLines = paymentLineIndexes.map((index: number) => allLines[index]);

    const payments = paymentLines.map((line: string) => {
        const source = line.replace(/^\d+\.\s*/, '').trim();

        // Parse pipe-delimited key:value pairs.
        // Label run is bounded with `{0,80}` so the greedy class can't be re-scanned from every
        // start position over a long input (the cause of super-linear backtracking). Real labels
        // fit within 80 chars, so matching behavior is unchanged.
        const fields: Record<string, string> = {};
        const pairRegex = /([A-Za-z][A-Za-z ]{0,80}):\s*([^|]+?)(?=\s*\||\s*$)/gi;
        let match;
        while ((match = pairRegex.exec(source)) !== null) {
            fields[match[1].trim().toLowerCase()] = match[2].trim();
        }

        return {
            contractId: fields['contract id'] || fields['contractid'] || '',
            scheduledDate: fields['scheduled date'] || fields['schedule date'] || fields['payment date'] || '',
            amount: fields['amount due'] || fields['amount'] || '-',
            status: fields['status'] || 'Pending',
            recordId: fields['record id'] || '-',
            paymentOId: fields['payment oid'] || '-',
            aspireContractId: fields['aspire contract id'] || '-',
            createdTime: fields['created time'] || '-',
        };
    });

    const firstPaymentLineIndex = paymentLineIndexes[0] ?? -1;
    const introLines = firstPaymentLineIndex > 0 ? allLines.slice(0, firstPaymentLineIndex) : [];
    const intro = introLines.length > 0 ? introLines.join(' ').trim() : null;

    const lastPaymentLineIndex = paymentLineIndexes[paymentLineIndexes.length - 1] ?? -1;
    const tailLines = lastPaymentLineIndex >= 0 ? allLines.slice(lastPaymentLineIndex + 1) : [];
    let followUp = tailLines.length > 0 ? tailLines.join('\n') : null;

    if (!followUp || !/navigate to/i.test(followUp)) {
        const _navIdx = payloadText.search(/For complete Scheduled Payment/i);
        const navigationBlockMatch = _navIdx >= 0 ? [payloadText.slice(_navIdx)] : null;
        if (navigationBlockMatch) {
            followUp = navigationBlockMatch[0].trim();
        }
    }

    return { payments, followUp, intro };
};

export const parseContracts = (
    text: string,
): {
    activeCount: string;
    closedCount: string;
    contracts: ContractSummary[];
    followUp: string | null;
    intro: string | null;
} | null => {
    // Structured JSON path: the API now returns contracts as an array. Map it directly
    // to the ContractSummary shape the renderer consumes, bypassing all text parsing.
    const parseStructuredContracts = (rawText: string) => {
        const trimmedText = rawText.trim();
        if (!trimmedText.startsWith('{') && !trimmedText.startsWith('[')) {
            return null;
        }

        const formatNextPaymentDate = (value: unknown) => formatDate(value);

        try {
            let parsedPayload: LooseRecord = JSON.parse(trimmedText);
            if (typeof parsedPayload?.data === 'string') {
                const nested = parsedPayload.data.trim();
                if (nested.startsWith('{') || nested.startsWith('[')) {
                    parsedPayload = JSON.parse(nested);
                }
            }

            const rows: LooseRecord[] | null = Array.isArray(parsedPayload?.data) ? parsedPayload.data : null;
            if (!rows || !rows.length) {
                return null;
            }

            // Invoice payloads also carry contract_id; explicitly reject them so the contracts
            // parser can't greedily claim open-invoice responses. Invoices are owned by parseInvoices.
            const looksLikeInvoices = rows.some(
                (row: LooseRecord) => row && (row.invoice_number !== undefined || row.due_date !== undefined),
            );
            if (looksLikeInvoices) {
                return null;
            }

            const looksLikeContracts = rows.some(
                (row: LooseRecord) => row && (row.contract_id !== undefined || row.IsLoan !== undefined),
            );
            if (!looksLikeContracts) {
                return null;
            }

            // Standard contract keys mapped to first-class fields below. Any other key the
            // API returns (e.g. past_due_amount on a "balance details" response) is surfaced
            // as a labeled additional-detail row rather than being dropped.
            const standardKeys = new Set([
                'contract_id',
                'isloan',
                'description',
                'monthly_payment',
                'next_payment_date',
                'status',
                'principal_amount',
                'last_payment_amount',
                'number_of_payments',
                'payments_done',
            ]);

            const humanizeKey = (key: string) =>
                key
                    .replace(/[_-]+/g, ' ')
                    .replace(/([a-z])([A-Z])/g, '$1 $2')
                    .trim()
                    .replace(/\b\w/g, char => char.toUpperCase());

            const buildExtraDetails = (row: LooseRecord) =>
                Object.keys(row || {})
                    .filter(key => !standardKeys.has(key.toLowerCase()))
                    .map(key => ({ label: humanizeKey(key), value: String(row[key] ?? '').trim() }))
                    .filter(detail => detail.value && detail.value.toLowerCase() !== 'null');

            const contractList: ContractSummary[] = rows
                .map((row: LooseRecord): ContractSummary | null => {
                    const contractId = String(row?.contract_id ?? '').trim();
                    if (!contractId) {
                        return null;
                    }

                    const totalPayments = Number(row?.number_of_payments);
                    const paymentsDone = Number(row?.payments_done);
                    const progress =
                        Number.isFinite(totalPayments) && Number.isFinite(paymentsDone)
                            ? `${paymentsDone} of ${totalPayments} payments`
                            : '';

                    // Only assert Loan/Lease when the API actually tells us; otherwise leave the
                    // badge empty so we don't mislabel a sparse "balance details" record.
                    const type = typeof row?.IsLoan === 'boolean' ? (row.IsLoan ? 'Loan' : 'Lease') : '';

                    return {
                        contractId,
                        type,
                        description: String(row?.description ?? '').trim(),
                        monthlyPayment: String(row?.monthly_payment ?? '').trim(),
                        nextPaymentOn: formatNextPaymentDate(row?.next_payment_date),
                        status: String(row?.status ?? '').trim(),
                        principalAmount: String(row?.principal_amount ?? '').trim(),
                        lastPayment: String(row?.last_payment_amount ?? '').trim(),
                        progress,
                        additionalDetails: buildExtraDetails(row),
                    };
                })
                .filter((contract: ContractSummary | null): contract is ContractSummary => Boolean(contract));

            if (!contractList.length) {
                return null;
            }

            // Only count contracts whose status is actually recognizable as active/closed.
            // The chatbot payload sometimes sends "Unknown" for every contract; in that case
            // the status is unreliable and we must NOT fabricate a count — surface "-" instead
            // of mislabeling every contract as Active.
            const isClosedStatus = (status: string) => status.toLowerCase().includes('closed');
            const isActiveStatus = (status: string) => status.toLowerCase().includes('active');
            const hasDeterminateStatus = contractList.some(
                contract => isActiveStatus(contract.status || '') || isClosedStatus(contract.status || ''),
            );
            const activeCount = hasDeterminateStatus
                ? String(contractList.filter(contract => isActiveStatus(contract.status || '')).length)
                : '-';
            const closedCount = hasDeterminateStatus
                ? String(contractList.filter(contract => isClosedStatus(contract.status || '')).length)
                : '-';

            // The count chips already convey the "N active / M closed" summary, so suppress
            // only that redundant header. Any other message (e.g. "Here are your balance
            // details:") is surfaced as an intro line so the user keeps the API's context.
            const message = typeof parsedPayload?.message === 'string' ? parsedPayload.message.trim() : '';
            const isRedundantCountMessage =
                /^(?:there are|you have)\s+\d+\s+active\s+and\s+\d+\s+closed\s+contracts?/i.test(message);
            const intro = message && !isRedundantCountMessage ? message : null;

            return {
                activeCount,
                closedCount,
                contracts: contractList,
                followUp: typeof parsedPayload?.follow_up === 'string' ? parsedPayload.follow_up : null,
                intro,
            };
        } catch {
            return null;
        }
    };

    const structuredContracts = parseStructuredContracts(text);
    if (structuredContracts) {
        return structuredContracts;
    }

    const payloadText = extractRawPayloadText(text);
    const lines = payloadText
        .split('\n')
        .map((line: string) => line.trim())
        .filter(Boolean);

    const hasContractsHeader = /you have\s+\d+\s+active\s+contracts?\s*(?:,|and)\s*\d+\s+closed\s+contracts?/i.test(
        payloadText,
    );
    const numberedContractLines = lines.filter(
        (line: string) =>
            /^\d+\./.test(line) &&
            /contract\s+id\s*:/i.test(line) &&
            (/monthly\s+payment\s*:/i.test(line) ||
                /next\s+payment\s+on\s*:/i.test(line) ||
                /remaining\s+payments\s*:/i.test(line)),
    );

    if (!hasContractsHeader && numberedContractLines.length === 0) {
        return null;
    }

    const parseCount = (value: string) => {
        const normalizedValue = value.replace(/,/g, '').trim();
        const parsedValue = Number.parseInt(normalizedValue, 10);
        return Number.isNaN(parsedValue) ? null : parsedValue;
    };

    const header = payloadText.match(
        /You have\s+(\d+)\s+Active\s+contracts?\s*(?:,|and)\s*(\d+)\s+Closed\s+contracts?:?/i,
    );
    const contractCandidateLines = lines.filter(
        (line: string) =>
            /contract\s+id\s*:/i.test(line) &&
            (/monthly\s+payment\s*:/i.test(line) ||
                /next\s+payment\s+on\s*:/i.test(line) ||
                /remaining\s+payments\s*:/i.test(line) ||
                /payments\s+done\s*:/i.test(line)),
    );

    const numberedLines =
        numberedContractLines.length > 0
            ? numberedContractLines
            : contractCandidateLines.length > 0
              ? contractCandidateLines
              : lines.filter((line: string) => /^\d+\./.test(line));

    const parseField = parseFieldByLabel;

    const normalizeFieldLabel = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

    const extractKeyValuePairs = (source: string) => {
        // Label runs are bounded with `{0,80}` so the greedy class can't be re-scanned from
        // every start position over a long input — that unbounded re-scan causes super-linear
        // backtracking. Real labels fit within 80 chars, so matching behavior is unchanged.
        const pairRegex =
            /([A-Za-z][A-Za-z ()/-]{0,80}):\s*([^|\n]+?)(?=\s*(?:\||\n)\s*[A-Za-z][A-Za-z ()/-]{0,80}:|\s*$)/g;
        const pairs: Array<{ label: string; value: string }> = [];
        let match = pairRegex.exec(source);
        while (match) {
            const label = (match[1] || '').trim();
            const value = (match[2] || '').trim();
            if (label && value) {
                pairs.push({ label, value });
            }
            match = pairRegex.exec(source);
        }
        return pairs;
    };

    const buildAdditionalDetails = (pairs: Array<{ label: string; value: string }>) => {
        const excludedFieldKeys = new Set(
            [
                'contract id',
                'contractid',
                'type',
                'contract type',
                'description',
                'monthly payment',
                'monthly',
                'next payment date',
                'next payment on',
                'next due',
                'status',
                'principal amount',
                'principal',
                'last payment on',
                'last payment date',
                'last payment',
                'progress',
                'payment progress',
                'number of payments',
                'total payments',
                'payments done',
                'remaining payments',
            ].map(normalizeFieldLabel),
        );

        return pairs.filter(({ label, value }) => {
            const normalizedKey = normalizeFieldLabel(label);
            const normalizedValue = value.trim().toLowerCase();
            return (
                !excludedFieldKeys.has(normalizedKey) &&
                normalizedValue !== '' &&
                normalizedValue !== '-' &&
                normalizedValue !== 'n/a'
            );
        });
    };

    const contractsFromNumberedLines = numberedLines
        .map((line: string): ContractSummary | null => {
            const source = line.replace(/^\d+\.\s*/, '').trim();
            const keyValuePairs = extractKeyValuePairs(source);

            const contractId = parseField(source, 'Contract ID');
            if (!contractId) {
                return null;
            }

            const totalPayments = parseCount(
                parseField(source, 'Number of Payments') || parseField(source, 'Total Payments'),
            );
            const paymentsDone = parseCount(parseField(source, 'Payments Done'));
            const remainingPayments = parseCount(parseField(source, 'Remaining Payments'));
            const derivedProgress = computeDerivedProgress(totalPayments, paymentsDone, remainingPayments);

            return {
                contractId,
                type: parseField(source, 'Type') || parseField(source, 'Contract Type'),
                description: parseField(source, 'Description'),
                monthlyPayment: parseField(source, 'Monthly Payment') || parseField(source, 'Monthly'),
                nextPaymentOn:
                    parseField(source, 'Next Payment Date') ||
                    parseField(source, 'Next Payment On') ||
                    parseField(source, 'Next Due'),
                status: parseField(source, 'Status'),
                principalAmount: parseField(source, 'Principal Amount') || parseField(source, 'Principal'),
                lastPayment:
                    parseField(source, 'Last Payment On') ||
                    parseField(source, 'Last Payment Date') ||
                    parseField(source, 'Last Payment'),
                progress: parseField(source, 'Progress') || parseField(source, 'Payment Progress') || derivedProgress,
                additionalDetails: buildAdditionalDetails(keyValuePairs),
            };
        })
        .filter((contract: ContractSummary | null): contract is ContractSummary => Boolean(contract));

    const rawBlocks = payloadText
        .split(/\n\s*\n/)
        .map((block: string) => block.trim())
        .filter(Boolean)
        .filter((block: string) => block.includes('Contract ID:'));

    const contractsFromBlocks = rawBlocks
        .map((block: string): ContractSummary => {
            const getField = (label: string) => {
                const match = block.match(new RegExp(`${label}:\\s*([^\\n]+)`, 'i'));
                return match?.[1]?.trim() || '';
            };

            const keyValuePairs = extractKeyValuePairs(block);

            const progressFromText = block.match(/(\d+\s*of\s*\d+\s*payments?)/i)?.[1]?.trim() || '';
            const lastPaymentFromText =
                block
                    .match(/last\s*payment(?:\s*on|\s*date)?\s*(?::\s*)?([0-9]{1,2}[/-][0-9]{1,2}[/-][0-9]{2,4})/i)?.[1]
                    ?.trim() || '';
            const totalPayments = parseCount(getField('Number of Payments') || getField('Total Payments'));
            const paymentsDone = parseCount(getField('Payments Done'));
            const remainingPayments = parseCount(getField('Remaining Payments'));
            const derivedProgress = computeDerivedProgress(totalPayments, paymentsDone, remainingPayments);

            return {
                contractId: getField('Contract ID').split('|')[0].trim(),
                type: getField('Type') || getField('Contract Type'),
                description: getField('Description'),
                monthlyPayment: getField('Monthly Payment') || getField('Monthly'),
                nextPaymentOn: getField('Next Payment Date') || getField('Next Payment On') || getField('Next Due'),
                status: getField('Status'),
                principalAmount: getField('Principal Amount') || getField('Principal'),
                lastPayment:
                    getField('Last Payment On') ||
                    getField('Last Payment Date') ||
                    getField('Last Payment') ||
                    lastPaymentFromText,
                progress: getField('Progress') || getField('Payment Progress') || progressFromText || derivedProgress,
                additionalDetails: buildAdditionalDetails(keyValuePairs),
            };
        })
        .filter((contract: ContractSummary) => Boolean(contract.contractId));

    const contracts = contractsFromNumberedLines.length > 0 ? contractsFromNumberedLines : contractsFromBlocks;

    if (!contracts.length) {
        return null;
    }

    const intro =
        lines.find((line: string) => /^here are the details for\s+.+contract/i.test(line)) ||
        lines.find((line: string) =>
            /^you have\s+\d+\s+active\s+contracts?\s*(?:,|and)\s*\d+\s+closed\s+contracts?/i.test(line),
        ) ||
        null;

    const followUp =
        extractFollowUpQuestion(lines) ||
        lines.find(
            (line: string) =>
                !line.match(/^Contract ID/) && !line.match(/You have/) && line.toLowerCase().includes('would you like'),
        ) ||
        null;

    const derivedActiveCount = contracts.filter(
        (contract: ContractSummary) => !(contract.status || '').toLowerCase().includes('closed'),
    ).length;
    const derivedClosedCount = contracts.length - derivedActiveCount;

    return {
        activeCount: header?.[1] || String(derivedActiveCount),
        closedCount: header?.[2] || String(derivedClosedCount),
        contracts,
        followUp,
        intro,
    };
};

export const parseContractFinancialSummary = (text: string): ContractFinancialSummaryResponse | null => {
    const payloadText = extractRawPayloadText(text);
    const lines = payloadText
        .split('\n')
        .map((line: string) => line.trim())
        .filter(Boolean);

    const numberedLines = lines.filter(
        (line: string) =>
            /^\d+\./.test(line) &&
            /contract\s+id\s*:/i.test(line) &&
            /financed\s+amount\s*:/i.test(line) &&
            /paid\s*\(\s*principal\s*\)\s*:/i.test(line) &&
            /remaining\s+amount\s*:/i.test(line),
    );

    if (!numberedLines.length) {
        return null;
    }

    const parseField = (source: string, label: string) => {
        const escapedLabel = escapeRegexLabel(label);
        const regex = new RegExp(
            `${escapedLabel}\\s*:\\s*([^|]+?)(?=\\s*\\|\\s*[A-Za-z][A-Za-z ()/-]{0,80}:|\\s*$)`,
            'i',
        );
        return source.match(regex)?.[1]?.trim() || '';
    };

    const items = numberedLines
        .map((line: string): ContractFinancialItem | null => {
            const source = line.replace(/^\d+\.\s*/, '').trim();
            const contractId = parseField(source, 'Contract ID');
            if (!contractId) {
                return null;
            }

            return {
                contractId,
                financedAmount: parseField(source, 'Financed Amount'),
                paidPrincipal: parseField(source, 'Paid (Principal)'),
                remainingAmount: parseField(source, 'Remaining Amount'),
                status: parseField(source, 'Status'),
                description: parseField(source, 'Description'),
            };
        })
        .filter((item: ContractFinancialItem | null): item is ContractFinancialItem => Boolean(item));

    if (!items.length) {
        return null;
    }

    const firstNumberedIndex = lines.findIndex((line: string) => /^\d+\./.test(line));
    const intro =
        lines.slice(0, firstNumberedIndex >= 0 ? firstNumberedIndex : 1).find((line: string) => !/^\d+\./.test(line)) ||
        'Contract Information';

    const lastNumberedIdx = lines.reduce(
        (lastIndex: number, line: string, index: number) => (/^\d+\./.test(line) ? index : lastIndex),
        -1,
    );
    const followUpLines = lastNumberedIdx >= 0 ? lines.slice(lastNumberedIdx + 1) : [];
    const followUp = extractFollowUpQuestion(followUpLines) || followUpLines[0] || null;

    return {
        intro,
        items,
        followUp,
    };
};

// AutoPay status responses arrive in two shapes that must render identically:
//   JSON: { message, data: [{ contract_id, status?, autopay_enabled: true|false }] }
//   Text: "Contract ID: X | AutoPay Enabled: Yes"
// Normalize both to the same metric-card result (one "AUTOPAY ENABLED: Yes/No" tile per contract),
// so the heavy "Contracts Overview" card and raw boolean values are never shown for this intent.
export const parseAutoPayStatus = (text: string): ContractMetricListResponse | null => {
    // Coerce true/false/"true"/"yes"/1 -> "Yes", the rest -> "No".
    const toYesNo = (value: unknown): string => {
        if (value === true) return 'Yes';
        if (value === false) return 'No';
        const normalized = String(value ?? '')
            .trim()
            .toLowerCase();
        if (!normalized) return '';
        return ['yes', 'true', 'y', '1', 'enabled', 'on'].includes(normalized) ? 'Yes' : 'No';
    };

    const trimmed = text.trim();

    // --- JSON array shape ---
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
            let parsed: LooseRecord = JSON.parse(trimmed);
            if (typeof parsed?.data === 'string') {
                const nested = parsed.data.trim();
                if (nested.startsWith('{') || nested.startsWith('[')) {
                    parsed = JSON.parse(nested);
                }
            }
            const rows: LooseRecord[] | null = Array.isArray(parsed?.data)
                ? parsed.data
                : Array.isArray(parsed)
                  ? parsed
                  : null;
            // Only own rows that are specifically about AutoPay (carry an autopay flag) and aren't
            // richer contract records (no monthly_payment / principal / next_payment_date, etc.).
            const isAutoPayRow = (row: LooseRecord) =>
                row &&
                typeof row === 'object' &&
                (row.autopay_enabled !== undefined || row.autopay !== undefined || row.auto_pay !== undefined);
            if (!rows || !rows.length || !rows.every(isAutoPayRow)) {
                return null;
            }

            const autopayRows: ContractMetricRow[] = rows
                .map((row: LooseRecord): ContractMetricRow | null => {
                    const contractId = String(row?.contract_id ?? row?.contractId ?? '').trim();
                    const value = toYesNo(row?.autopay_enabled ?? row?.autopay ?? row?.auto_pay);
                    if (!contractId || !value) {
                        return null;
                    }
                    return { contractId, metrics: [{ label: 'AutoPay Enabled', value }] };
                })
                .filter((row: ContractMetricRow | null): row is ContractMetricRow => Boolean(row));

            if (!autopayRows.length) {
                return null;
            }

            return {
                intro: extractEnvelopeMessage(text) || 'Here is your AutoPay status',
                rows: autopayRows,
                followUp: null,
            };
        } catch {
            return null;
        }
    }

    // --- Text shape: "Contract ID: X | AutoPay Enabled: Yes" (one per line) ---
    const payloadText = extractPayloadText(text);
    const lines = payloadText
        .split('\n')
        .map((line: string) => line.trim())
        .filter(Boolean);

    const autopayLineRegex = /autopay\s*(?:enabled\s*)?:/i;
    const autopayLines = lines.filter((line: string) => /\|/.test(line) && autopayLineRegex.test(line));
    if (!autopayLines.length) {
        return null;
    }

    const rows: ContractMetricRow[] = autopayLines
        .map((line: string): ContractMetricRow | null => {
            const source = line.replace(/^\d+\.\s*/, '').trim();
            const idMatch = source.match(/contract\s*id\s*(?:[:#]\s*)?([A-Za-z0-9[\]_-]+)/i);
            const leadToken = source.split('|')[0].trim();
            const contractId = idMatch?.[1]?.trim() || (!leadToken.includes(':') && leadToken ? leadToken : '');
            const valueMatch = source.match(/autopay\s*(?:enabled\s*)?:\s*([^|]+?)\s*(?:\||$)/i);
            const value = toYesNo(valueMatch?.[1]);
            if (!contractId || !value) {
                return null;
            }
            return { contractId, metrics: [{ label: 'AutoPay Enabled', value }] };
        })
        .filter((row: ContractMetricRow | null): row is ContractMetricRow => Boolean(row));

    if (!rows.length) {
        return null;
    }

    return {
        intro: extractEnvelopeMessage(text) || 'Here is your AutoPay status',
        rows,
        followUp: null,
    };
};

export const parseContractMetricList = (text: string): ContractMetricListResponse | null => {
    const payloadText = extractPayloadText(text);
    const lines = payloadText
        .split('\n')
        .map((line: string) => line.trim())
        .filter(Boolean);

    // Scheduled-payment payloads can share "Contract ID | ..." formatting.
    // Let parseScheduledPayments own those so the scheduled payment cards render.
    if (
        /scheduled\s+date\s*:/i.test(payloadText) &&
        /amount\s+due\s*:/i.test(payloadText) &&
        /status\s*:/i.test(payloadText)
    ) {
        return null;
    }

    if (!lines.length) {
        return null;
    }

    // Resolve a contract id from a pipe row in either shape:
    //   - Labeled:   "Contract ID: 1017520 | Past Due Amount: $X"
    //   - Bare lead: "1017520 | Past Due Amount: $X"  (id is the token before the first "|",
    //                 with no "Field:" label of its own)
    const metricListContractId = (source: string): string => {
        const labeledMatch = source.match(/contract\s*id\s*(?:[:#]\s*)?([A-Za-z0-9-]+)/i);
        if (labeledMatch) {
            return labeledMatch[1].trim();
        }
        const leadToken = source.split('|')[0].trim();
        return !leadToken.includes(':') && /^[A-Za-z0-9-]+$/.test(leadToken) ? leadToken : '';
    };

    // A row qualifies when it is pipe-delimited and we can resolve a contract id (labeled or bare).
    const numberedLines = lines.filter(
        (line: string) => /\|/.test(line) && Boolean(metricListContractId(line.replace(/^\d+\.\s*/, '').trim())),
    );

    if (!numberedLines.length) {
        const narrativeText = lines.join(' ');
        // A bot prompt asking the user to supply a contract ID (e.g. "Please provide the
        // contract ID ... so I can look up the asset serial number for you.") merely mentions
        // "asset serial number" — it doesn't contain one. Treat it as plain text, not a card.
        const isRequestForContractId =
            /\bplease\s+(provide|share|enter|specify)\b/i.test(narrativeText) ||
            /\b(provide|share)\b[^\n]*\bcontract\s*id\b/i.test(narrativeText) ||
            /\bso\s+(?:that\s+)?i\s+can\s+(?:look\s*up|retrieve|find|fetch)\b/i.test(narrativeText);
        if (isRequestForContractId) {
            return null;
        }
        const hasVinNarrative = /vehicle\s+identification\s+number|\bvin\b/i.test(narrativeText);
        const hasAssetSerialNarrative = /asset\s+serial\s+numbers?\s+on\s+file|asset\s+serial\s+number/i.test(
            narrativeText,
        );
        const contractIdMatch = narrativeText.match(/contract\s+id\s*([A-Za-z0-9-]+)/i);
        const vinMatch = narrativeText.match(/\bis\s*([A-Za-z0-9-]{6,})\b/i);
        const assetDescMatch = narrativeText.match(/asset\s+is\s+a?n?\s+(.+?)\s+made\s+by/i);
        // Capture ends on a non-space char so the class `\s` can't overlap the lookahead's `\s*`
        // (avoids ambiguous-whitespace backtracking); .trim() below keeps behavior identical.
        const makeMatch = narrativeText.match(/made\s+by\s+([A-Za-z0-9][A-Za-z0-9 -]{0,80})(?=\s*(?:,\s*model|\.|$))/i);
        const modelMatch = narrativeText.match(/model\s+([A-Za-z0-9-]+)/i);

        const serialContractIdMatch =
            narrativeText.match(/for\s+contract\s+id\s*(?:[:#]\s*)?([A-Za-z0-9-]+)/i) ||
            narrativeText.match(/contract\s+id\s*(?:[:#]\s*)?([A-Za-z0-9-]+)/i);
        const serialLineMatch = payloadText.match(/asset\s+serial\s+numbers?\s+on\s+file\s+(?:is|are)\s*([^\n.?]+)/i);
        const serialFallbackMatch = payloadText.match(/asset\s+serial\s+numbers?\s*(?:[:#]\s*)?([^\n.?]+)/i);
        const serialValue = (serialLineMatch?.[1] || serialFallbackMatch?.[1] || '').trim();

        if (!hasVinNarrative || !contractIdMatch || !vinMatch) {
            if (!hasAssetSerialNarrative || !serialContractIdMatch || !serialValue) {
                return null;
            }

            const row: ContractMetricRow = {
                contractId: serialContractIdMatch[1].trim(),
                metrics: [{ label: 'Serial Number', value: serialValue }],
            };

            const followUp = extractFollowUpQuestion(lines) || null;
            const intro =
                lines.find(
                    (line: string) => !/^would\s+you\s+like/i.test(line) && !/for\s+contract\s+id\s*[:#]?/i.test(line),
                ) || 'Asset serial number details';

            return {
                intro,
                rows: [row],
                followUp,
            };
        }

        const row: ContractMetricRow = {
            contractId: contractIdMatch[1].trim(),
            metrics: [
                { label: 'Serial Number (VIN)', value: vinMatch[1].trim() },
                ...(assetDescMatch ? [{ label: 'Asset Description', value: assetDescMatch[1].trim() }] : []),
                ...(makeMatch ? [{ label: 'Make', value: makeMatch[1].trim() }] : []),
                ...(modelMatch ? [{ label: 'Model', value: modelMatch[1].trim() }] : []),
            ],
        };

        const followUp = extractFollowUpQuestion(lines) || null;
        const intro = lines.find((line: string) => !/^would\s+you\s+like/i.test(line)) || 'Contract information';

        return {
            intro,
            rows: [row],
            followUp,
        };
    }

    const parseFields = (source: string) => {
        const fields: Record<string, string> = {};
        // Label runs are bounded with `{0,80}` to prevent the greedy class from being re-scanned
        // from every start position over a long input (the cause of super-linear backtracking).
        // Real labels fit within 80 chars, so matching behavior is unchanged.
        const pairRegex = /([A-Za-z][A-Za-z ()/-]{0,80}):\s*([^|]+?)(?=\s*\|\s*[A-Za-z][A-Za-z ()/-]{0,80}:|$)/g;
        let match = pairRegex.exec(source);
        while (match) {
            fields[match[1].trim().toLowerCase()] = match[2].trim();
            match = pairRegex.exec(source);
        }
        return fields;
    };

    // An "Asset" field can be a comma-separated blob carrying its own sub-fields, e.g.
    //   "CNST-Track Loader, Serial Number: B4SB37333, Make: Bobcat, Model: T66, Year: 2024,
    //    Location: 3919 Ladson Rd, Ladson, SC, USA"
    // Explode it into discrete Asset Name / Serial Number / Make / Model / Year / Location metrics
    // so the asset card renders them in their own tiles instead of dropping them.
    const explodeAssetBlob = (raw: string): Record<string, string> => {
        const out: Record<string, string> = {};
        const labelPair = (label: string) => {
            const esc = escapeRegexLabel(label);
            const re = new RegExp(`${esc}\\s*:\\s*(.+?)(?=\\s*,\\s*[A-Za-z][A-Za-z ()/-]{0,80}:|$)`, 'i');
            return raw.match(re)?.[1]?.trim() || '';
        };
        const serial = labelPair('Serial Number');
        const make = labelPair('Make');
        const model = labelPair('Model');
        const year = labelPair('Year');
        // Location is the trailing field and its value itself contains commas (street, city,
        // state, country), so capture everything after "Location:" to end-of-string.
        const location = raw.match(/Location\s*:\s*(.+)$/i)?.[1]?.trim() || '';
        // The leading text before the first "Label:" pair is the asset name (e.g. "CNST-Track
        // Loader"). Stored as Asset Name so the row's own "Description" stays the card title and
        // the asset name shows as the subtitle.
        const assetName = raw.split(/,\s*[A-Za-z][A-Za-z ()/-]{0,80}:/)[0].trim();
        if (assetName) out['asset name'] = assetName;
        if (serial) out['serial number'] = serial;
        if (make) out['make'] = make;
        if (model) out['model'] = model;
        if (year) out['year'] = year;
        if (location) out['location'] = location;
        return out;
    };

    const rows = numberedLines
        .map((line: string): ContractMetricRow | null => {
            const source = line.replace(/^\d+\.\s*/, '').trim();
            const fields = parseFields(source);
            const contractId =
                fields['contract id'] || fields['aspire id'] || fields['contract'] || metricListContractId(source);

            if (!contractId) {
                return null;
            }

            // Expand an "Asset: <blob>" field into its sub-fields (serial/make/model/year), then
            // drop the raw blob so it isn't shown twice (once exploded, once as the Location fallback).
            const expandedFields: Record<string, string> = { ...fields };
            const assetBlob = fields['asset'];
            if (assetBlob && /serial\s*number\s*:/i.test(assetBlob)) {
                Object.assign(expandedFields, explodeAssetBlob(assetBlob));
                delete expandedFields['asset'];
            }

            const metrics = Object.entries(expandedFields)
                .filter(([key]) => !['contract id', 'aspire id', 'contract'].includes(key))
                .map(([label, value]) => ({
                    label: label
                        .split(/\s+/)
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' '),
                    value,
                }))
                .filter(metric => Boolean(metric.value));

            if (!metrics.length) {
                return null;
            }

            return {
                contractId,
                metrics,
            };
        })
        .filter((row: ContractMetricRow | null): row is ContractMetricRow => Boolean(row));

    if (!rows.length) {
        return null;
    }

    const intro =
        extractEnvelopeMessage(text) ||
        lines.find(
            (line: string) =>
                !/contract\s*id\s*:/i.test(line) && !/^\d+\./.test(line) && !/^would\s+you\s+like/i.test(line),
        ) ||
        'Contract information';

    const lastNumberedIdx = lines.reduce(
        (lastIndex: number, line: string, index: number) => (/^\d+\./.test(line) ? index : lastIndex),
        -1,
    );
    const followUpLines = lastNumberedIdx >= 0 ? lines.slice(lastNumberedIdx + 1) : [];
    const followUp = followUpLines.length > 0 ? followUpLines.join('\n') : null;

    return {
        intro,
        rows,
        followUp,
    };
};

export const parseContractSnapshotPayload = (text: string): ContractSnapshotResponse | null => {
    const normalizeDate = (value: unknown) => {
        const raw = String(value || '').trim();
        if (!raw) {
            return '-';
        }
        return formatDate(raw);
    };

    const toCurrencyLike = (value: unknown) => {
        const raw = String(value ?? '').trim();
        return raw || '-';
    };

    try {
        const trimmed = text.trim();
        if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
            return null;
        }

        const parsedPayload: LooseRecord = JSON.parse(trimmed);
        let payloadData: unknown = parsedPayload?.data ?? parsedPayload;

        if (typeof payloadData === 'string') {
            const nested = payloadData.trim();
            if (nested.startsWith('{') || nested.startsWith('[')) {
                payloadData = JSON.parse(nested);
            }
        }

        const records: LooseRecord[] = Array.isArray(payloadData)
            ? (payloadData as LooseRecord[])
            : payloadData
              ? [payloadData as LooseRecord]
              : [];

        const contractCandidates = records.filter(
            (record: LooseRecord) =>
                record &&
                (record.AspireId || record.ContractId || record.Id) &&
                (record.EMIAmount ||
                    record.OutstandingBalance ||
                    record.RemainingPrincipal ||
                    record.NextPaymentDate ||
                    record.PastDue),
        );

        if (!contractCandidates.length) {
            return null;
        }

        const items: ContractSnapshotItem[] = contractCandidates.map((record: LooseRecord) => ({
            contractId: String(record.AspireId || record.ContractId || record.Id || '-'),
            status: String(record.StatusDescr || record.Status || '-'),
            accountType: String(record.AccountType || '-'),
            assetDesc: String(record.AssetDesc || '-'),
            emiAmount: toCurrencyLike(record.EMIAmount),
            outstandingBalance: toCurrencyLike(record.OutstandingBalance),
            remainingPrincipal: toCurrencyLike(record.RemainingPrincipal),
            pastDue: toCurrencyLike(record.PastDue),
            nextPaymentDate: normalizeDate(record.NextPaymentDate),
            nextPaymentAmount: toCurrencyLike(record.NextPaymentAmount),
            autoPay: record.AutoPay === true ? 'Enabled' : record.AutoPay === false ? 'Disabled' : '-',
            paymentsProgress:
                Number.isFinite(Number(record.NumberOfPayments)) && Number.isFinite(Number(record.PaymentsReceived))
                    ? `${record.PaymentsReceived} / ${record.NumberOfPayments}`
                    : '-',
            maturityDate: normalizeDate(record.MaturityDate),
            lastPaymentDate: normalizeDate(record.LastPaymentDate),
            lastPaymentAmount: toCurrencyLike(record.LastPaymentAmount),
        }));

        return {
            intro: 'Contract Snapshot',
            items,
        };
    } catch {
        return null;
    }
};

export const parseNextPaymentDue = (text: string): NextPaymentDueResponse | null => {
    const payloadText = extractPayloadText(text);
    const lines = payloadText
        .split('\n')
        .map((line: string) => line.trim())
        .filter(Boolean);

    if (!lines.length) {
        return null;
    }

    const parseField = parseFieldByLabel;

    // Pull the contract id from a pipe row. Two shapes are supported:
    //   - Labeled:   "Contract ID: 1017520 | Next Payment Date: ..."
    //   - Bare lead: "1017520 | Next Payment Date: ..."  (id is the token before the first "|",
    //                 with no "Field:" label of its own)
    const parseContractIdFromPipeRow = (source: string): string => {
        const labeled = parseField(source, 'Contract ID');
        if (labeled) {
            return labeled;
        }
        const leadToken = source.split('|')[0].trim();
        // Accept only a bare id token (no "Label:" colon) like "1017520" or "CV18029".
        return !leadToken.includes(':') && /^[A-Za-z0-9-]+$/.test(leadToken) ? leadToken : '';
    };

    // Format A — pipe-delimited fields:
    //   Contract ID: X | Next Payment Date: MM/DD/YYYY | Next Payment Amount: $Y | Frequency: Monthly
    //   X | Next Payment Date: MM/DD/YYYY | Next Payment Amount: $Y | Frequency: Monthly  (bare id)
    // A line belongs here only when it carries both a next payment date and amount (the signature),
    // plus a resolvable contract id (labeled or bare-leading). Anything missing the amount is a
    // richer contract record others own.
    const pipePaymentLines = lines.filter(
        (line: string) =>
            /next\s+payment\s+date\s*:/i.test(line) &&
            /next\s+payment\s+amount\s*:/i.test(line) &&
            Boolean(parseContractIdFromPipeRow(line.replace(/^\d+\.\s*/, '').trim())),
    );

    // Format B — narrative bullet lines:
    //   - Contract ID 1017520: $994.58 due on 04/29/2026
    // Captures the contract id, amount, and the "due on" date without the labeled fields above.
    const narrativeRegex =
        /contract\s*id\s*(?:[:#]\s*)?([A-Za-z0-9-]+)\s*(?:[:-]\s*)?(?:\$\s*)?([\d,]+(?:\.\d+)?)\s*due\s*on\s*([0-9]{1,2}[/-][0-9]{1,2}[/-][0-9]{2,4}|\d{4}-\d{2}-\d{2})/i;

    let items: NextPaymentDueItem[];

    if (pipePaymentLines.length) {
        items = pipePaymentLines
            .map((line: string): NextPaymentDueItem | null => {
                const source = line.replace(/^\d+\.\s*/, '').trim();
                const contractId = parseContractIdFromPipeRow(source);
                if (!contractId) {
                    return null;
                }

                return {
                    contractId,
                    nextPaymentDate: formatDate(parseField(source, 'Next Payment Date')),
                    nextPaymentAmount: parseField(source, 'Next Payment Amount'),
                    frequency: parseField(source, 'Frequency'),
                };
            })
            .filter((item: NextPaymentDueItem | null): item is NextPaymentDueItem => Boolean(item));
    } else {
        items = lines
            .map((line: string): NextPaymentDueItem | null => {
                const match = line.match(narrativeRegex);
                if (!match) {
                    return null;
                }

                return {
                    contractId: match[1].trim(),
                    nextPaymentDate: formatDate(match[3]),
                    nextPaymentAmount: match[2].trim(),
                    frequency: '',
                };
            })
            .filter((item: NextPaymentDueItem | null): item is NextPaymentDueItem => Boolean(item));
    }

    if (!items.length) {
        return null;
    }

    const followUp = extractFollowUpQuestion(lines) || null;

    // Prefer the envelope's own header message (e.g. "Here are your payment details:") when the
    // payload arrived as JSON with a separate `message`. Otherwise prefer a genuine narrative intro
    // line (one that isn't itself a contract row, a bullet, the follow-up, or a numbered entry).
    // Never fall back to a contract DATA row as the title — use a clean default instead, so the
    // first card row isn't echoed as a redundant heading.
    const envelopeHeader = extractEnvelopeMessage(text);
    const isContractRow = (line: string) => /contract\s*id\s*:/i.test(line) || narrativeRegex.test(line);
    const intro =
        envelopeHeader ||
        lines.find(
            (line: string) =>
                !isContractRow(line) &&
                !/^\d+\./.test(line) &&
                !/^[-•*]/.test(line) &&
                !/^would\s+you\s+like/i.test(line) &&
                line !== followUp,
        ) ||
        'Next payment due';

    return {
        intro,
        items,
        followUp,
    };
};

// "What is the start and end date for my contracts" returns a narrative bullet list, one per line:
//   - Contract ID 1010298: Start Date 12/22/2023, End Date 12/22/2029
// Its own response shape (a contract id plus a start/maturity date pair, no payment fields), so it
// gets a dedicated parser + card instead of falling through to the generic plain-text renderer.
export const parseContractDateRange = (text: string): ContractDateRangeResponse | null => {
    const payloadText = extractPayloadText(text);
    const lines = payloadText
        .split('\n')
        .map((line: string) => line.trim())
        .filter(Boolean);

    if (!lines.length) {
        return null;
    }

    const dateToken = '([0-9]{1,2}[/-][0-9]{1,2}[/-][0-9]{2,4}|\\d{4}-\\d{2}-\\d{2})';
    // Format A — labeled: "Contract ID X: Start Date MM/DD/YYYY, End Date MM/DD/YYYY"
    const rangeRegex = new RegExp(
        `contract\\s*id\\s*(?:[:#]\\s*)?([A-Za-z0-9-]+)[^\\n]*?start\\s*date\\s*(?:[:#]\\s*)?${dateToken}[^\\n]*?end\\s*date\\s*(?:[:#]\\s*)?${dateToken}`,
        'i',
    );
    // Format B — narrative single contract: "Contract ID C30321 starts on 07/29/2025 and ends on 07/29/2029."
    const narrativeRangeRegex = new RegExp(
        `contract\\s*id\\s*(?:[:#]\\s*)?([A-Za-z0-9-]+)[^\\n]*?starts?\\s+on\\s+${dateToken}[^\\n]*?ends?\\s+on\\s+${dateToken}`,
        'i',
    );
    const matchDateRange = (line: string) => line.match(rangeRegex) || line.match(narrativeRangeRegex);

    const items = lines
        .map((line: string): ContractDateRangeItem | null => {
            const match = matchDateRange(line);
            if (!match) {
                return null;
            }

            return {
                contractId: match[1].trim(),
                startDate: formatDate(match[2]),
                endDate: formatDate(match[3]),
            };
        })
        .filter((item: ContractDateRangeItem | null): item is ContractDateRangeItem => Boolean(item));

    if (!items.length) {
        return null;
    }

    const followUp = extractFollowUpQuestion(lines) || null;

    // Same header treatment as the other contract-list cards: prefer a separate narrative intro
    // line. For the single-contract narrative ("... starts on ... and ends on ...") the only line
    // IS the data row, so there's no separate header — fall back to a generic title instead of
    // echoing the sentence (the card already shows the dates).
    const headerLine = lines.find(
        (line: string) =>
            !matchDateRange(line) &&
            !/^\d+\./.test(line) &&
            !/^[-•*]/.test(line) &&
            !/^would\s+you\s+like/i.test(line) &&
            line !== followUp,
    );
    const intro =
        extractEnvelopeMessage(text) ||
        headerLine ||
        (items.length > 1 ? 'Contract dates' : 'Contract start & end dates');

    return {
        intro,
        items,
        followUp,
    };
};

// Generic single-metric list. The chatbot returns several "one value per contract" responses in the
// same narrative shape:
//   Here are the interest rates for your contracts:
//   - Contract ID 1017520: 2.90%
// Rather than a bespoke parser per metric, this one owns ANY such list: it reads the metric name from
// the "Here are the <metric> for your contracts" header and pairs each bullet's contract id with its
// single value. Richer shapes (next payment "$X due on DATE", start/end date pairs) are claimed by
// their dedicated parsers, which run earlier in the dispatch order.
export const parseContractMetricBullets = (text: string): ContractMetricBulletResponse | null => {
    const payloadText = extractPayloadText(text);
    const lines = payloadText
        .split('\n')
        .map((line: string) => line.trim())
        .filter(Boolean);

    if (!lines.length) {
        return null;
    }

    // A bullet row carries exactly one value after the contract id. Reject rows that look like the
    // richer dedicated shapes so this generic parser doesn't claim them.
    const isRicherShape = (line: string) =>
        /due\s*on\b/i.test(line) || /start\s*date/i.test(line) || /end\s*date/i.test(line) || /\|/.test(line);

    const bulletRegex = /contract\s*id\s*(?:[:#]\s*)?([A-Za-z0-9-]+)\s*[:#-]\s*(.+)$/i;

    // Some values carry their own inline label before the figure, e.g. "Remaining Principal
    // $48,555.49". Split that leading label off so the tile shows just the figure ("$48,555.49")
    // and we can reuse the inline label as the tile heading (avoids the duplicated
    // "REMAINING BALANCE / Remaining Principal $48,555.49" reading).
    const splitInlineLabel = (raw: string): { label: string; value: string } => {
        // Label class uses literal spaces and ends on a non-space char ([A-Za-z()/-]) so it
        // can't overlap the following `\s+` separator — avoids ambiguous-whitespace backtracking.
        // .trim() on the captured label keeps behavior identical for multi-word labels.
        const inlineMatch = raw.match(/^([A-Za-z][A-Za-z ()/-]{0,80})\s+((?:\$|-?\d).*)$/);
        if (inlineMatch) {
            return { label: inlineMatch[1].trim(), value: inlineMatch[2].trim() };
        }
        return { label: '', value: raw };
    };

    // "X payments completed out of Y" reads cleanly as "X / Y" under a short "PAYMENTS" label.
    // Returns a tidied value + label when the completion phrasing is present, else nothing.
    const tidyCompletionRatio = (raw: string): { label: string; value: string } | null => {
        const completionMatch = raw.match(/^(\d[\d,]*)\s+payments?\s+completed\s+out\s+of\s+(\d[\d,]*)/i);
        if (completionMatch) {
            return { label: 'Payments', value: `${completionMatch[1]} / ${completionMatch[2]}` };
        }
        return null;
    };

    const inlineLabels: string[] = [];
    const items = lines
        .map((line: string): ContractMetricBulletItem | null => {
            if (isRicherShape(line)) {
                return null;
            }
            const match = line.replace(/^[-•*\d.\s]+/, '').match(bulletRegex);
            if (!match) {
                return null;
            }
            const rawValue = match[2].trim();
            const completion = tidyCompletionRatio(rawValue);
            const { label, value } = completion || splitInlineLabel(rawValue);
            if (!value) {
                return null;
            }
            if (label) {
                inlineLabels.push(label);
            }
            return {
                contractId: match[1].trim(),
                value,
            };
        })
        .filter((item: ContractMetricBulletItem | null): item is ContractMetricBulletItem => Boolean(item));

    // A single contract is better served by the contract-details card; only treat this as a metric
    // list when there are at least two contracts sharing one metric.
    if (items.length < 2) {
        return null;
    }

    const followUp = extractFollowUpQuestion(lines) || null;

    const headerLine =
        lines.find(
            (line: string) =>
                !bulletRegex.test(line.replace(/^[-•*\d.\s]+/, '')) &&
                !/^[-•*]/.test(line) &&
                !/^would\s+you\s+like/i.test(line) &&
                line !== followUp,
        ) || '';

    // Pull the metric name out of "Here are the <metric> for your contracts" and singularize a
    // trailing plural ("rates" -> "rate") so the tile label reads naturally.
    const headerMetricMatch = headerLine.match(/here\s+are\s+(?:the\s+)?(.+?)\s+for\s+(?:your|all)/i);
    const rawMetric = (headerMetricMatch?.[1] || '').replace(/\([^)]*\)/g, '').trim();
    const singularize = (value: string) =>
        value
            .split(/\s+/)
            .map((word, index, arr) =>
                index === arr.length - 1 ? word.replace(/ies$/i, 'y').replace(/s$/i, '') : word,
            )
            .join(' ');
    // Prefer a consistent inline label (present on every parsed item) since it names the exact figure
    // shown; otherwise fall back to the metric name pulled from the header.
    const allShareInlineLabel = inlineLabels.length === items.length && inlineLabels.length > 0;
    const sharedInlineLabel =
        allShareInlineLabel && inlineLabels.every(label => label === inlineLabels[0]) ? inlineLabels[0] : '';
    const metricLabel = (sharedInlineLabel || (rawMetric ? singularize(rawMetric) : 'Value')).toUpperCase();

    const intro = extractEnvelopeMessage(text) || headerLine || `Contract ${rawMetric || 'details'}`;

    return {
        intro,
        metricLabel,
        items,
        followUp,
    };
};

// Single-contract metric stated as prose, e.g.
//   "The remaining balance (principal) on contract ID C30321 is $39,744.92."
//   "The interest rate on contract ID C30321 is 6.59%."
// Beautifies the bare sentence into the same labeled metric tile we use for the list responses.
export const parseSingleContractMetric = (text: string): SingleContractMetricResponse | null => {
    const payloadText = extractPayloadText(text);
    const lines = payloadText
        .split('\n')
        .map((line: string) => line.trim())
        .filter(Boolean);

    if (!lines.length) {
        return null;
    }

    // "The <metric> on contract ID <id> is <value>." — value runs to the end of the sentence.
    const metricRegex =
        /^the\s+(.+?)\s+on\s+contract\s*id\s*(?:[:#]\s*)?([A-Za-z0-9-]+)\s+is\s+(.+?)\.?(?=\s+would\s+you\s+like|$)/i;

    let parsed: { metric: string; contractId: string; value: string } | null = null;
    for (const line of lines) {
        const match = line.match(metricRegex);
        if (match) {
            parsed = { metric: match[1].trim(), contractId: match[2].trim(), value: match[3].trim() };
            break;
        }
    }

    if (!parsed || !parsed.value) {
        return null;
    }

    // "remaining balance (principal)" -> "REMAINING BALANCE (PRINCIPAL)".
    const metricLabel = parsed.metric.replace(/\s+/g, ' ').trim().toUpperCase();
    const followUp = lines.find((line: string) => /^would\s+you\s+like/i.test(line)) || null;

    return {
        contractId: parsed.contractId,
        metricLabel,
        value: parsed.value,
        followUp,
    };
};

// "What assets are linked to contract ID X" returns a bullet list of assets, one per line:
//   - CNST-Track Loader, Serial Number: B4SB37333, Make: Bobcat, Model: T66, Year: 2024
// followed by a shared location sentence ("Both assets are located at ..."). Each line is the asset
// name plus comma-separated Label: value pairs — its own shape, so it gets a dedicated parser/card
// instead of the single-asset VIN branch in parseContractMetricList that drops all but the first.
export const parseLinkedAssets = (text: string): LinkedAssetResponse | null => {
    const payloadText = extractPayloadText(text);
    const lines = payloadText
        .split('\n')
        .map((line: string) => line.trim())
        .filter(Boolean);

    if (!lines.length) {
        return null;
    }

    // Only own asset-linked-to-contract responses — not arbitrary comma lists. Covers both
    // "assets linked to contract" and "asset serial numbers linked to contract" phrasings.
    if (!/assets?\b[^\n]*\blinked\s+to\s+contract/i.test(payloadText)) {
        return null;
    }

    const parsePair = (source: string, label: string) => {
        const escapedLabel = escapeRegexLabel(label);
        // Value runs until the next "Label:" pair or end of line; commas inside the value are kept.
        const regex = new RegExp(`${escapedLabel}\\s*[:#]\\s*(.+?)(?=,\\s*[A-Za-z][A-Za-z ()/-]{0,80}[:#]|$)`, 'i');
        const value = source.match(regex)?.[1]?.trim() || '';
        return value && value.toLowerCase() !== 'n/a' ? value : '';
    };

    // Format B — "serial for name": "- B4SB37333 for CNST-Track Loader". A bare serial token
    // followed by "for <asset name>", no labeled fields.
    const serialForNameRegex = /^([A-Za-z0-9-]{4,})\s+for\s+(.+)$/i;

    const bulletLines = lines.filter((line: string) => /^[-•*]/.test(line));

    const items = bulletLines
        .map((line: string): LinkedAssetItem | null => {
            const source = line.replace(/^[-•*\s]+/, '').trim();

            // Format A — labeled comma-separated pairs:
            //   "- CNST-Track Loader, Serial Number: B4SB37333, Make: Bobcat, ..."
            if (/serial\s*number\s*[:#]/i.test(source)) {
                const name = source.split(/,\s*[A-Za-z][A-Za-z ()/-]{0,80}[:#]/)[0].trim();
                return {
                    name,
                    serialNumber: parsePair(source, 'Serial Number'),
                    make: parsePair(source, 'Make'),
                    model: parsePair(source, 'Model'),
                    year: parsePair(source, 'Year'),
                    location: '',
                };
            }

            // Format B — "<serial> for <name>".
            const serialForName = source.match(serialForNameRegex);
            if (serialForName) {
                return {
                    name: serialForName[2].trim(),
                    serialNumber: serialForName[1].trim(),
                    make: '',
                    model: '',
                    year: '',
                    location: '',
                };
            }

            return null;
        })
        .filter((item: LinkedAssetItem | null): item is LinkedAssetItem =>
            Boolean(item && (item.serialNumber || item.name)),
        );

    if (!items.length) {
        return null;
    }

    // A shared location sentence applies to every asset (e.g. "Both assets are located at ...").
    const locationLine = lines.find((line: string) => /located\s+at/i.test(line));
    const sharedLocation = locationLine?.match(/located\s+at\s+(.+?)\.?$/i)?.[1]?.trim() || '';
    if (sharedLocation) {
        items.forEach((item: LinkedAssetItem) => {
            item.location = sharedLocation;
        });
    }

    const intro =
        lines.find((line: string) => /assets?\b[^\n]*\blinked\s+to\s+contract/i.test(line)) || 'Linked assets';

    // Don't echo the header back as a follow-up (it's the only non-bullet line here, so the generic
    // extractor would otherwise surface it again under the cards).
    const followUpCandidate = extractFollowUpQuestion(lines) || null;
    const followUp = followUpCandidate && followUpCandidate.trim() !== intro.trim() ? followUpCandidate : null;

    return {
        intro,
        items,
        followUp,
    };
};

export const parseSupportRequests = (text: string) => {
    // Structured JSON path: the API now returns support requests as an array. Map it
    // directly to the SupportRequestItem shape the renderer consumes.
    const parseStructuredSupportRequests = (rawText: string) => {
        const trimmedText = rawText.trim();
        if (!trimmedText.startsWith('{') && !trimmedText.startsWith('[')) {
            return null;
        }

        const formatCreatedDate = (value: unknown) => {
            if (!value || typeof value !== 'string') {
                return '-';
            }
            return formatDate(value);
        };

        try {
            let parsedPayload: LooseRecord = JSON.parse(trimmedText);
            if (typeof parsedPayload?.data === 'string') {
                const nested = parsedPayload.data.trim();
                if (nested.startsWith('{') || nested.startsWith('[')) {
                    parsedPayload = JSON.parse(nested);
                }
            }

            const rows: LooseRecord[] | null = Array.isArray(parsedPayload?.data) ? parsedPayload.data : null;
            if (!rows || !rows.length) {
                return null;
            }

            const looksLikeSupportRequests = rows.some(
                (row: LooseRecord) => row && (row.ticket_number !== undefined || row.request_type !== undefined),
            );
            if (!looksLikeSupportRequests) {
                return null;
            }

            const requests: SupportRequestItem[] = rows
                .map((row: LooseRecord): SupportRequestItem | null => {
                    const ticketNumber = String(row?.ticket_number ?? '').trim();
                    const subject = String(row?.subject ?? '').trim();
                    const requestType = String(row?.request_type ?? '').trim();

                    // Allow requests even without ticket numbers, using subject/type as fallback
                    if (!ticketNumber && !subject && !requestType) {
                        return null;
                    }

                    const status = String(row?.status_label ?? '').trim();
                    const contract = String(row?.contract_id ?? row?.contract ?? '').trim();
                    return {
                        ticketNumber: ticketNumber || '-',
                        status,
                        hasStatus: !!status,
                        subject: subject || 'Support request',
                        date: formatCreatedDate(row?.created_date),
                        type: requestType || '-',
                        contract,
                        hasContract: !!contract,
                        description: String(row?.description ?? '').trim(),
                    };
                })
                .filter((request: SupportRequestItem | null): request is SupportRequestItem => Boolean(request));

            if (!requests.length) {
                return null;
            }

            return {
                intro: typeof parsedPayload?.message === 'string' ? parsedPayload.message : 'Support requests',
                requests,
                followUp: typeof parsedPayload?.follow_up === 'string' ? parsedPayload.follow_up : null,
            };
        } catch {
            return null;
        }
    };

    const structuredSupportRequests = parseStructuredSupportRequests(text);
    if (structuredSupportRequests) {
        return structuredSupportRequests;
    }

    const payloadText = extractRawPayloadText(text);
    const normalizedText = payloadText.toLowerCase();

    // Match structured ticket responses even when "support request" phrase is absent.
    const hasTicketNumber = /ticket\s*number\s*:/i.test(payloadText) || /ticket\s*#\s*[a-z0-9-]+/i.test(payloadText);
    const hasSupportSpecificFields =
        /request\s*type\s*:/i.test(payloadText) ||
        /subject\s*:/i.test(payloadText) ||
        /status\s*:/i.test(payloadText) ||
        /created\s*date\s*:/i.test(payloadText) ||
        /description\s*:/i.test(payloadText);
    const mentionsSupportContext =
        normalizedText.includes('support request') || normalizedText.includes('support ticket');

    if (!hasTicketNumber || (!mentionsSupportContext && !hasSupportSpecificFields)) {
        return null;
    }

    const lines: string[] = payloadText
        .split('\n')
        .map((line: string) => line.trim())
        .filter(Boolean);

    const intro =
        lines.find((line: string) => !line.match(/^\d+\./) && !line.toLowerCase().includes('would you like')) ||
        'Support requests';
    const numberedLines = lines.filter((line: string) => /^\d+\./.test(line));
    // NOSONAR: Non-greedy inner quantifiers prevent ReDoS; greedy outer * ensures all continuation lines match
    const blockMatches: string[] = payloadText.match(/\d+\.[^\n]*?(?:\n(?!\n|\d+\.|Would)[^\n]*?)*$/gm) || []; // NOSONAR

    const readField = (raw: string, field: string) => {
        const escapedField = escapeRegexLabel(field);
        const regex = new RegExp(`${escapedField}\\s*:\\s*([^|]+?)(?=\\s*\\|\\s*[A-Za-z][A-Za-z -]{0,80}:|\\s*$)`, 'i');
        return raw.match(regex)?.[1]?.trim() || '';
    };

    const parsePipeFields = (raw: string) => {
        const fields: Record<string, string> = {};
        // Label runs are bounded with `{0,80}` to prevent the greedy class from being re-scanned
        // from every start position over a long input (the cause of super-linear backtracking).
        // Real labels fit within 80 chars, so matching behavior is unchanged.
        const pairRegex = /([A-Za-z][A-Za-z -]{0,80}):\s*([^|]+?)(?=\s*\|\s*[A-Za-z][A-Za-z -]{0,80}:|\s*$)/g;
        let match = pairRegex.exec(raw);
        while (match) {
            fields[match[1].trim().toLowerCase()] = match[2].trim();
            match = pairRegex.exec(raw);
        }
        return fields;
    };

    const requestsFromSingleLine = numberedLines
        .map((line: string) => {
            const body = line.replace(/^\d+\.\s*/, '').trim();
            const pipeFields = parsePipeFields(body);
            const getPipeField = (...keys: string[]) => {
                return keys.map(key => pipeFields[key]).find(Boolean) || '';
            };

            const ticketNumber =
                getPipeField('ticket number', 'ticket #', 'ticket no', 'ticket') ??
                readField(body, 'Ticket Number') ??
                /Ticket\s*(?:#|Number|No)?\s*[:#]?\s*([-a-z0-9]{1,100})/i.exec(body)?.[1]?.trim() ??
                '';

            if (!ticketNumber) {
                return null;
            }

            const rawStatus = getPipeField('status') || readField(body, 'Status');
            const contractFromSubject =
                /for\s+contract\s*[:#]?\s*([-a-z0-9]{1,100})/i.exec(getPipeField('subject') ?? '')?.[1] ?? '';
            const rawContract =
                getPipeField('contract', 'contract id') || readField(body, 'Contract') || contractFromSubject;
            return {
                ticketNumber,
                status: rawStatus || '',
                hasStatus: !!rawStatus,
                subject: getPipeField('subject', 'request subject') || readField(body, 'Subject') || 'Support request',
                date:
                    getPipeField('created date', 'date', 'created on') ||
                    readField(body, 'Created Date') ||
                    readField(body, 'Date') ||
                    '-',
                type:
                    getPipeField('request type', 'type') ||
                    readField(body, 'Request Type') ||
                    readField(body, 'Type') ||
                    '-',
                contract: rawContract || '',
                hasContract: !!rawContract,
                description: getPipeField('description', 'details') || readField(body, 'Description') || '',
            };
        })
        .filter(Boolean) as SupportRequestItem[];

    const requestsFromBlocks = blockMatches
        .map((block: string) => {
            const blockLines: string[] = block
                .split('\n')
                .map((line: string) => line.trim())
                .filter(Boolean);

            if (!blockLines.length) {
                return null;
            }

            const firstLine = blockLines[0].replace(/^\d+\.\s*/, '').trim();
            const ticketNumber =
                readField(firstLine, 'Ticket Number') ||
                firstLine.match(/Ticket\s*(?:#\s*)?([A-Za-z0-9-]+)/i)?.[1]?.trim() ||
                '';

            if (!ticketNumber) {
                return null;
            }

            const fields = blockLines.slice(1).reduce(
                (acc: Record<string, string>, line: string) => {
                    const match = line.match(/^([^:]+):\s*(.+)$/);
                    if (!match) {
                        return acc;
                    }
                    acc[match[1].trim().toLowerCase()] = match[2].trim();
                    return acc;
                },
                {} as Record<string, string>,
            );

            const rawStatus = fields.status || '';
            const rawContract = fields.contract || '';
            return {
                ticketNumber,
                status: rawStatus,
                hasStatus: !!rawStatus,
                subject: fields.subject || 'Support request',
                date: fields.date || '-',
                type: fields['request type'] || fields.type || '-',
                contract: rawContract,
                hasContract: !!rawContract,
                description: fields.description || '',
            };
        })
        .filter(Boolean) as SupportRequestItem[];

    // Prefer single-line parsing for pipe-delimited chatbot payloads.
    // Block parsing is kept as fallback for multi-line legacy responses.
    const requests = requestsFromSingleLine.length > 0 ? requestsFromSingleLine : requestsFromBlocks;

    if (!requests.length) {
        return null;
    }

    const followUpCandidates = lines.filter(
        (line: string) =>
            !line.match(/^\d+\./) &&
            !line.toLowerCase().includes('support request') &&
            !line.toLowerCase().includes('breakdown') &&
            !line.toLowerCase().startsWith('here are') &&
            !line.toLowerCase().startsWith('below are') &&
            !line.toLowerCase().startsWith('subject:') &&
            !line.toLowerCase().startsWith('request type:') &&
            !line.toLowerCase().startsWith('description:') &&
            !line.toLowerCase().startsWith('date:') &&
            line !== intro,
    );

    const followUp = extractFollowUpQuestion(followUpCandidates) || followUpCandidates[0] || null;

    return { intro, requests, followUp };
};

// Hoisted out of parsePaymentHistory so its branching isn't counted against the parent; behavior
// unchanged (same closure over formatDate).
export const parseStructuredPaymentHistory = (rawText: string) => {
    const trimmedText = rawText.trim();
    if (!trimmedText.startsWith('{') && !trimmedText.startsWith('[')) {
        return null;
    }

    const normalizeDate = (value: string) => {
        if (!value) return '-';
        return formatDate(value);
    };

    const joinUnique = joinUniqueValues;

    try {
        let parsedPayload: LooseRecord = JSON.parse(trimmedText);

        if (typeof parsedPayload?.data === 'string') {
            const nestedData = parsedPayload.data.trim();
            if (nestedData.startsWith('{') || nestedData.startsWith('[')) {
                parsedPayload = JSON.parse(nestedData);
            }
        }

        const rows: LooseRecord[] | null = Array.isArray(parsedPayload?.data) ? parsedPayload.data : null;
        if (!rows || !rows.length) {
            return null;
        }

        const looksLikePaymentHistory = rows.some(
            (row: LooseRecord) =>
                row &&
                (row.referenceNumber ||
                    row.paymentDate ||
                    row.amount ||
                    row.paymentType ||
                    Array.isArray(row.invoices)),
        );

        if (!looksLikePaymentHistory) {
            return null;
        }

        const payments: PaymentItem[] = rows.map((row: LooseRecord) => {
            const invoices: LooseRecord[] = Array.isArray(row?.invoices) ? (row.invoices as LooseRecord[]) : [];
            const statuses = invoices
                .map((invoice: LooseRecord) => String(invoice?.status || '').trim())
                .filter(Boolean);

            const breakdowns: PaymentBreakdownItem[] = invoices.map((invoice: LooseRecord) => ({
                dueDate: normalizeDate(String(invoice?.dueDate || '')),
                invoiceNumber: String(invoice?.invoiceHeaderOId || '-'),
                transactionType: String(invoice?.transactionCode || '-'),
                invoiceMethod: String(invoice?.paymentType || row?.paymentType || '-'),
                status: String(invoice?.status || '-'),
                appliedAmount: String(invoice?.appliedAmount ?? '-'),
            }));

            return {
                contractId: String(row?.contractId || '-'),
                paymentDate: normalizeDate(String(row?.paymentDate || '')),
                referenceNumber: String(row?.referenceNumber || '-'),
                amount: String(row?.amount || '-'),
                method: String(row?.paymentType || '-'),
                dueDate: joinUnique(breakdowns.map(item => item.dueDate)),
                invoiceNumber: joinUnique(breakdowns.map(item => item.invoiceNumber)),
                transactionType: joinUnique(breakdowns.map(item => item.transactionType)),
                invoiceMethod: joinUnique(breakdowns.map(item => item.invoiceMethod)),
                status: statuses.length ? joinUnique(statuses) : '-',
                appliedAmount: joinUnique(breakdowns.map(item => item.appliedAmount)),
                breakdowns,
            };
        });

        const followUpRaw = String(parsedPayload?.follow_up || '').trim();
        const followUpLines = followUpRaw
            ? followUpRaw
                  .split('\n')
                  .map((line: string) => line.trim())
                  .filter(Boolean)
            : [];

        return {
            intro: String(parsedPayload?.summary || parsedPayload?.message || 'Payment records'),
            payments,
            followUpLines,
        };
    } catch {
        return null;
    }
};

export const parsePaymentHistory = (text: string) => {
    const structuredPaymentHistory = parseStructuredPaymentHistory(text);
    if (structuredPaymentHistory) {
        return structuredPaymentHistory;
    }

    const payloadText = extractRawPayloadText(text, true);

    const structuredPaymentHistoryFromPayloadText = parseStructuredPaymentHistory(payloadText);
    if (structuredPaymentHistoryFromPayloadText) {
        return structuredPaymentHistoryFromPayloadText;
    }

    const normalizedText = payloadText.toLowerCase();
    const hasPaymentSpecificFields =
        normalizedText.includes('reference number:') ||
        normalizedText.includes('payment effective date:') ||
        normalizedText.includes('applied amount:') ||
        normalizedText.includes('transaction type:');

    const isPaymentContext =
        normalizedText.includes('payment history') ||
        normalizedText.includes('recent payment') ||
        normalizedText.includes('payment details') ||
        hasPaymentSpecificFields;

    if (!isPaymentContext) {
        return null;
    }

    const lines = payloadText
        .split('\n')
        .map((line: string) => line.trim())
        .filter(Boolean);

    if (!lines.length) {
        return null;
    }

    const intro = lines[0] || 'Payment records';
    const numberedLines = lines.filter((line: string) => line.match(/^\d+\./));
    const isPaymentRecordLine = (line: string) => {
        const lowered = line.toLowerCase();
        return (
            /^\d+\./.test(line) &&
            (lowered.includes('payment date:') ||
                lowered.includes('amount:') ||
                lowered.includes('reference number:') ||
                lowered.includes('method:') ||
                lowered.includes('invoice number:'))
        );
    };
    const paymentRecordLines = numberedLines.filter(isPaymentRecordLine);
    const guidanceNumberedLines = numberedLines.filter((line: string) => !isPaymentRecordLine(line));

    const parseFields = (raw: string) => {
        const source = raw.replace(/^\d+\.\s*/, '').trim();
        const fields: Record<string, string> = {};

        // Prefer the API's pipe-separated payload; keep comma-separated fallback for older responses.
        const pairRegex = source.includes('|')
            ? /([A-Za-z][A-Za-z ]{0,80}):\s*([^|]+?)(?=\s*\|\s*[A-Za-z][A-Za-z ]{0,80}:|$)/g
            : /([A-Za-z][A-Za-z ]{0,80}):\s*([^,]+?)(?=,\s*[A-Za-z][A-Za-z ]{0,80}:|$)/g;

        let match = pairRegex.exec(source);
        while (match) {
            fields[match[1].trim().toLowerCase()] = match[2].trim();
            match = pairRegex.exec(source);
        }

        return fields;
    };

    const hasMeaningfulValue = (value: string) => {
        const normalized = (value || '').trim().toLowerCase();
        return Boolean(
            normalized && normalized !== '-' && normalized !== 'n/a' && normalized !== 'na' && normalized !== 'none',
        );
    };

    const parseFromNumbered = paymentRecordLines
        .map((line: string) => {
            const fields = parseFields(line);
            const contractId = fields['contract id'] || fields.contract || '-';
            const amount = fields.amount || '-';
            const paymentDate =
                fields['payment effective date'] ||
                fields['payment date'] ||
                fields.date ||
                fields['scheduled date'] ||
                '-';
            const referenceNumber = fields['reference number'] || fields.reference || fields['transaction id'] || '-';
            const method = fields.method || fields['payment method'] || '-';
            const dueDate = fields['due date'] || '-';
            const invoiceNumber = fields['invoice number'] || fields.invoice || '-';
            const transactionType = fields['transaction type'] || fields['transaction'] || '-';
            const invoiceMethod = fields['invoice method'] || '-';
            const status = fields.status || '-';
            const appliedAmount = fields['applied amount'] || '-';

            // Ignore numbered guidance lines that are not actual payment records.
            const hasPrimaryPaymentFields =
                hasMeaningfulValue(amount) ||
                hasMeaningfulValue(paymentDate) ||
                hasMeaningfulValue(referenceNumber) ||
                hasMeaningfulValue(method) ||
                hasMeaningfulValue(invoiceNumber);

            if (!hasPrimaryPaymentFields) {
                return null;
            }

            return {
                contractId,
                paymentDate,
                referenceNumber,
                amount,
                method,
                dueDate,
                invoiceNumber,
                transactionType,
                invoiceMethod,
                status,
                appliedAmount,
            };
        })
        .filter(Boolean) as PaymentItem[];

    const hasAnyBreakdownFields = parseFromNumbered.some(
        payment =>
            hasMeaningfulValue(payment.dueDate) ||
            hasMeaningfulValue(payment.transactionType) ||
            hasMeaningfulValue(payment.invoiceMethod) ||
            hasMeaningfulValue(payment.appliedAmount),
    );

    const joinUnique = joinUniqueValues;

    const payments = hasAnyBreakdownFields
        ? (() => {
              const groupedPaymentsMap = new Map<string, PaymentItem>();

              parseFromNumbered.forEach(payment => {
                  const paymentKey = [
                      payment.contractId,
                      payment.paymentDate,
                      payment.referenceNumber,
                      payment.amount,
                      payment.method,
                  ]
                      .map(value => value.trim().toLowerCase())
                      .join('|');

                  const breakdown: PaymentBreakdownItem = {
                      dueDate: payment.dueDate,
                      invoiceNumber: payment.invoiceNumber,
                      transactionType: payment.transactionType,
                      invoiceMethod: payment.invoiceMethod,
                      status: payment.status,
                      appliedAmount: payment.appliedAmount,
                  };

                  const existing = groupedPaymentsMap.get(paymentKey);
                  if (!existing) {
                      groupedPaymentsMap.set(paymentKey, {
                          ...payment,
                          breakdowns: [breakdown],
                      });
                      return;
                  }

                  const breakdownSignature = [
                      breakdown.dueDate,
                      breakdown.invoiceNumber,
                      breakdown.transactionType,
                      breakdown.invoiceMethod,
                      breakdown.status,
                      breakdown.appliedAmount,
                  ]
                      .map(value => value.trim().toLowerCase())
                      .join('|');

                  const hasBreakdown = (existing.breakdowns || []).some(item => {
                      const existingSignature = [
                          item.dueDate,
                          item.invoiceNumber,
                          item.transactionType,
                          item.invoiceMethod,
                          item.status,
                          item.appliedAmount,
                      ]
                          .map(value => value.trim().toLowerCase())
                          .join('|');
                      return existingSignature === breakdownSignature;
                  });

                  if (!hasBreakdown) {
                      existing.breakdowns = [...(existing.breakdowns || []), breakdown];
                  }
              });

              return Array.from(groupedPaymentsMap.values()).map(payment => {
                  const breakdowns = payment.breakdowns || [];
                  const hasBreakdowns = breakdowns.length > 0;
                  return {
                      ...payment,
                      dueDate: hasBreakdowns ? joinUnique(breakdowns.map(item => item.dueDate)) : payment.dueDate,
                      invoiceNumber: hasBreakdowns
                          ? joinUnique(breakdowns.map(item => item.invoiceNumber))
                          : payment.invoiceNumber,
                      transactionType: hasBreakdowns
                          ? joinUnique(breakdowns.map(item => item.transactionType))
                          : payment.transactionType,
                      invoiceMethod: hasBreakdowns
                          ? joinUnique(breakdowns.map(item => item.invoiceMethod))
                          : payment.invoiceMethod,
                      status: hasBreakdowns ? joinUnique(breakdowns.map(item => item.status)) : payment.status,
                      appliedAmount: hasBreakdowns
                          ? joinUnique(breakdowns.map(item => item.appliedAmount))
                          : payment.appliedAmount,
                  };
              });
          })()
        : parseFromNumbered.map(payment => ({
              ...payment,
              breakdowns: [],
          }));
    if (!payments.length) {
        return null;
    }

    const lastNumberedIndex = lines.reduce((lastIndex: number, line: string, index: number) => {
        return /^\d+\./.test(line) ? index : lastIndex;
    }, -1);

    const tailLines =
        lastNumberedIndex >= 0
            ? lines.slice(lastNumberedIndex + 1).filter((line: string) => !line.match(/^\d+\./))
            : [];
    const guidanceLines = guidanceNumberedLines
        .map((line: string) => line.replace(/^\d+\.\s*/, '').trim())
        .filter(Boolean);
    const followUpQuestion = extractFollowUpQuestion(tailLines);
    const followUpLines = followUpQuestion ? [followUpQuestion] : [...guidanceLines, ...tailLines];

    return { intro, payments, followUpLines };
};

export const parseAmortizationSchedule = (text: string): AmortizationScheduleResponse | null => {
    const payloadText = extractRawPayloadText(text);
    const normalizedText = payloadText.toLowerCase();
    const hasNumberedAmortizationRows = /^\d+\./m.test(payloadText);
    const hasAmortizationMetricFields =
        /payment\s*:/i.test(payloadText) ||
        /principal\s*:/i.test(payloadText) ||
        /interest\s*:/i.test(payloadText) ||
        /remaining\s+balance\s*:/i.test(payloadText);
    const isAmortizationContext =
        ((normalizedText.includes('amortization schedule') || normalizedText.includes('amortisation schedule')) &&
            (hasNumberedAmortizationRows || hasAmortizationMetricFields)) ||
        normalizedText.includes('principal paid') ||
        normalizedText.includes('interest paid');

    if (!isAmortizationContext) {
        return null;
    }

    const lines = payloadText
        .split('\n')
        .map((line: string) => line.trim())
        .filter((line: string) => Boolean(line));

    if (!lines.length) {
        return null;
    }

    const intro = lines[0] ?? 'Amortization Schedule';
    const contractId = /contract id[:#]?\s*([-a-z0-9]+)/i.exec(intro)?.[1] ?? '';
    const contractName = /\(([^)]+)\)/.exec(intro)?.[1]?.trim() ?? '';

    // Look for both numbered items and pipe-delimited date items
    const firstDataIndex = lines.findIndex((line: string) => /^\d+\.|^Date:\s*\d{2}-\d{2}-\d{4}/i.test(line));
    const summaryLines = firstDataIndex >= 0 ? lines.slice(1, firstDataIndex) : lines.slice(1);

    const stats: Array<{ label: string; value: string }> = [];
    const narrativeLines: string[] = [];

    summaryLines.forEach((line: string) => {
        const cleanedLine = line.replace(/^[-•]\s*/, '').trim();
        const keyValueMatch = cleanedLine.match(/^([^:]{2,80}):\s{0,20}(.{1,500})$/);

        if (keyValueMatch) {
            stats.push({
                label: keyValueMatch[1].trim(),
                value: keyValueMatch[2].trim(),
            });
            return;
        }

        if (cleanedLine) {
            narrativeLines.push(cleanedLine);
        }
    });

    const annualTotalLine = lines.find((line: string) => /^annual\s+total\s*-/i.test(line)) || '';

    // Parse both numbered and pipe-delimited amortization items
    const upcomingPayments = lines
        .filter((line: string) => /^\d+\.|^Date:\s*\d{2}-\d{2}-\d{4}/i.test(line))
        .map((line: string) => {
            const source = line.replace(/^\d+\.\s*/, '').trim();
            const segments = source
                .split('|')
                .map((segment: string) => segment.trim())
                .filter((segment: string) => Boolean(segment));
            const firstSegment = segments[0] || '';

            const readField = (label: string) => {
                const match = segments.find((segment: string) =>
                    segment.toLowerCase().startsWith(`${label.toLowerCase()}:`),
                );
                return match?.split(':').slice(1).join(':').trim() || '';
            };

            const paymentDate =
                firstSegment.match(/^\d{2}-\d{2}-\d{4}$/)?.[0] ||
                firstSegment.match(/date[:\s]+(\d{2}-\d{2}-\d{4})/i)?.[1] ||
                firstSegment.match(/payment date[:\s]+(.+)$/i)?.[1]?.trim() ||
                firstSegment;

            return {
                paymentDate,
                payment: readField('Payment'),
                principal: readField('Principal'),
                interest: readField('Interest'),
                remainingBalance: readField('Remaining Balance') || readField('Balance'),
                source: readField('Source'),
            };
        })
        .filter(
            (item: AmortizationUpcomingPaymentItem) =>
                item.paymentDate ||
                item.payment ||
                item.principal ||
                item.interest ||
                item.remainingBalance ||
                item.source,
        );

    if (!upcomingPayments.length && !stats.length && !narrativeLines.length) {
        return null;
    }

    const lastDataIndex = lines.reduce((lastIndex: number, line: string, index: number) => {
        return /^\d+\.|^Date:\s*\d{2}-\d{2}-\d{4}/i.test(line) ? index : lastIndex;
    }, -1);

    const followUpCandidates =
        lastDataIndex >= 0
            ? lines
                  .slice(lastDataIndex + 1)
                  .filter(
                      (line: string) =>
                          !line.match(/^\d+\./) && !/^date:/i.test(line) && !/^annual\s+total\s*-/i.test(line),
                  )
            : lines.filter(
                  (line: string) =>
                      !line.match(/^\d+\./) &&
                      !line.match(/^[-•]/) &&
                      line !== intro &&
                      !/^annual\s+total\s*-/i.test(line) &&
                      !/^date:/i.test(line),
              );

    const followUp = extractFollowUpQuestion(followUpCandidates) || followUpCandidates[0] || null;

    return {
        intro,
        contractId,
        contractName,
        summaryLine: [narrativeLines[0], annualTotalLine].filter(Boolean).join(' ') || '',
        stats,
        upcomingPayments,
        followUp,
    };
};

export const parseContractDetails = (text: string): ContractDetails | null => {
    const payloadText = extractRawPayloadText(text);
    const normalizedPayload = payloadText.toLowerCase();
    const lines = payloadText
        .split('\n')
        .map((line: string) => line.trim())
        .filter(Boolean);

    // The multi-contract "next payment due" list (Contract ID | Next Payment Date | Next Payment
    // Amount | Frequency, one line per contract) is owned by parseNextPaymentDue. Bail out here so
    // it doesn't get claimed as a single-contract details card that drops every row but the first.
    const nextPaymentDueLineCount = lines.filter(
        (line: string) =>
            /contract\s*id\s*:/i.test(line) &&
            /next\s+payment\s+date\s*:/i.test(line) &&
            /next\s+payment\s+amount\s*:/i.test(line),
    ).length;
    if (
        nextPaymentDueLineCount >= 2 &&
        !/billing\s+frequency\s*:/i.test(payloadText) &&
        !/outstanding\s+balance\s*:/i.test(payloadText) &&
        !/monthly\s+payment\s*:/i.test(payloadText)
    ) {
        return null;
    }

    const firstLine = lines[0] || '';
    const normalizedFirstLine = firstLine.replace(/^\d+\.\s*/, '');
    const looksLikePipeContractDetails =
        /contract\s*id\s*:/i.test(normalizedFirstLine) &&
        (/billing\s+frequency\s*:/i.test(normalizedFirstLine) ||
            /outstanding\s+balance\s*:/i.test(normalizedFirstLine) ||
            /monthly\s+payment\s*:/i.test(normalizedFirstLine) ||
            /next\s+payment\s+date\s*:/i.test(normalizedFirstLine) ||
            /next\s+payment\s+amount\s*:/i.test(normalizedFirstLine));

    const singleContractHeader = payloadText.match(
        /You have\s+(\d+)\s+Active\s+contracts?\s*(?:,|and)\s*(\d+)\s+Closed\s+contracts?:?/i,
    );
    const activeCount = Number.parseInt(singleContractHeader?.[1] || '0', 10);
    const closedCount = Number.parseInt(singleContractHeader?.[2] || '0', 10);
    // Only use contract-details view for a single contract when the text explicitly says
    // "here are the details for contract id" — i.e. the user asked for a specific contract.
    // List responses ("You have 1 active contract") should go through renderContracts instead.
    const isSingleContractDetailResponse =
        Boolean(singleContractHeader) &&
        activeCount + closedCount === 1 &&
        normalizedPayload.startsWith('here are the details for contract id');

    if (
        !normalizedPayload.startsWith('here are the details for contract id') &&
        !isSingleContractDetailResponse &&
        !looksLikePipeContractDetails
    ) {
        return null;
    }

    const introLine = lines.find((line: string) =>
        line.toLowerCase().startsWith('here are the details for contract id'),
    );
    const headerLine = lines.find((line: string) =>
        /^You have\s+\d+\s+Active\s+contracts?\s*(?:,|and)\s*\d+\s+Closed\s+contracts?:?/i.test(line),
    );
    const detailAnchor = introLine || headerLine || firstLine;

    const kvLines = lines.filter(
        (line: string) =>
            line !== introLine &&
            line !== headerLine &&
            line.includes(':') &&
            !/^You have\s+\d+\s+Active\s+contracts?/i.test(line),
    );

    const parsePairFields = (source: string) => {
        const fields: Record<string, string> = {};
        const pairRegex = source.includes('|')
            ? /([A-Za-z][A-Za-z -]{0,80}):\s*([^|]+?)(?=\s*\|\s*[A-Za-z][A-Za-z -]{0,80}:|$)/g
            : /([A-Za-z][A-Za-z -]{0,80}):\s*(.+)$/g;

        let match = pairRegex.exec(source);
        while (match) {
            fields[match[1].trim().toLowerCase()] = match[2].trim();
            match = pairRegex.exec(source);
        }

        return fields;
    };

    const kvMap = kvLines.reduce(
        (acc: Record<string, string>, line: string) => {
            const parsed = parsePairFields(line);
            return { ...acc, ...parsed };
        },
        {} as Record<string, string>,
    );

    const findField = (...keys: string[]) => keys.map(key => kvMap[key]).find(Boolean) || '';
    const parseCount = (value: string) => {
        const normalizedValue = value.replace(/,/g, '').trim();
        const parsedValue = Number.parseInt(normalizedValue, 10);
        return Number.isNaN(parsedValue) ? null : parsedValue;
    };
    const contractId = introLine?.match(/contract id\s*(?:[:#]\s*)?([a-z0-9-]+)/i)?.[1] || findField('contract id');
    const progressFromText = payloadText.match(/(\d+\s*of\s*\d+\s*payments?)/i)?.[1]?.trim() || '';
    const totalPayments = parseCount(findField('number of payments', 'total payments', 'term'));
    const paymentsDone = parseCount(findField('payments done'));
    const remainingPayments = parseCount(findField('remaining payments'));
    const derivedProgress = computeDerivedProgress(totalPayments, paymentsDone, remainingPayments);
    const followUpCandidates = lines.filter(
        (line: string) =>
            line !== detailAnchor &&
            line !== introLine &&
            line !== headerLine &&
            !line.match(/^([^:]+):\s*(.+)$/) &&
            !line.toLowerCase().startsWith('here are the details for contract id'),
    );

    const followUp = extractFollowUpQuestion(followUpCandidates) || followUpCandidates[0] || null;

    return {
        contractId,
        status: findField('status'),
        type: findField('type'),
        description: findField('description'),
        monthlyPayment: findField('monthly payment'),
        nextPaymentOn: findField(
            'next payment date',
            'next due',
            'next payment on',
            'next date',
            'next payment amount',
        ),
        principalAmount: findField('principal amount', 'principal'),
        lastPayment: findField('last payment amount'),
        progress: findField('progress', 'payment progress') || progressFromText || derivedProgress,
        followUp,
    };
};

export const parseGenericSections = (text: string): GenericSection[] | null => {
    const blocks = text
        .split(/\n\s*\n/)
        .map(block => block.trim())
        .filter(Boolean);

    if (!blocks.length) {
        return null;
    }

    const sections = blocks.map(block => {
        const rawLines = block
            .split('\n')
            .map(line => line.trim())
            .filter(Boolean);
        if (!rawLines.length) {
            return {
                title: '',
                summaryLines: [],
                keyValues: [],
                numberedItems: [],
            };
        }

        const firstLine = rawLines[0];
        const shouldUseFirstLineAsTitle =
            firstLine.endsWith(':') && !firstLine.match(/^\d+\./) && !firstLine.match(/^([^:]{2,40}):\s*.+$/);

        const shouldUseHeadingAsTitle =
            !shouldUseFirstLineAsTitle &&
            !firstLine.includes(':') &&
            !firstLine.match(/^\d+\./) &&
            firstLine.length <= 40 &&
            rawLines.length > 1 &&
            rawLines[1].includes('|') &&
            rawLines[1].includes(':');

        const title = shouldUseFirstLineAsTitle || shouldUseHeadingAsTitle ? firstLine.replace(/:$/, '') : '';
        const contentLines = shouldUseFirstLineAsTitle || shouldUseHeadingAsTitle ? rawLines.slice(1) : rawLines;

        const summaryLines: string[] = [];
        const keyValues: Array<{ label: string; value: string }> = [];
        const numberedItems: string[] = [];
        let usedTitleValueAsField = false;
        const normalizeFieldLabel = (value: string) =>
            value
                .trim()
                .replace(/^[•\-*]+\s*/, '')
                .trim();

        contentLines.forEach(rawLine => {
            let line = rawLine;
            // Marker digits bounded (no real numbered-list marker needs more than 6 digits); no
            // `\s*` before the value group since it would overlap with `.+` (both match spaces) —
            // an adjacent-quantifier shape that risks super-linear backtracking. `.trim()` below
            // already strips any leading whitespace.
            const numberedMatch = rawLine.match(/^\d{1,6}\.(.+)$/);
            if (numberedMatch) {
                const numberedContent = numberedMatch[1].trim();
                if (!numberedContent.includes(':')) {
                    numberedItems.push(numberedContent);
                    return;
                }
                line = numberedContent;
            }

            if (line.includes('|')) {
                const segments = line
                    .split('|')
                    .map(segment => segment.trim())
                    .filter(Boolean);

                let parsedAsKeyValues = false;

                segments.forEach(segment => {
                    const segmentKvMatch = segment.match(/^([^:]{2,40}):\s{0,20}(.{1,500})$/);
                    if (segmentKvMatch) {
                        keyValues.push({
                            label: normalizeFieldLabel(segmentKvMatch[1]),
                            value: segmentKvMatch[2].trim(),
                        });
                        parsedAsKeyValues = true;
                        return;
                    }

                    if (title && !usedTitleValueAsField) {
                        keyValues.push({
                            label: title,
                            value: segment,
                        });
                        usedTitleValueAsField = true;
                        parsedAsKeyValues = true;
                        return;
                    }

                    summaryLines.push(segment);
                });

                if (parsedAsKeyValues) {
                    return;
                }
            }

            // No `\s*` before the value group: it would overlap with `.+` (both match spaces),
            // an adjacent-quantifier shape that risks super-linear backtracking on pathological
            // whitespace runs. The trailing `.trim()` below already strips any leading whitespace.
            const keyValueMatch = line.match(/^([^:]{2,40}):\s{0,20}(.{1,500})$/);
            if (keyValueMatch) {
                keyValues.push({
                    label: normalizeFieldLabel(keyValueMatch[1]),
                    value: keyValueMatch[2].trim(),
                });
                return;
            }

            summaryLines.push(line);
        });

        return {
            title,
            summaryLines,
            keyValues,
            numberedItems,
        };
    });

    const hasRichStructure = sections.some(
        section =>
            section.title ||
            section.keyValues.length > 0 ||
            section.numberedItems.length > 0 ||
            section.summaryLines.length > 1,
    );

    // Plain instructional step-by-step responses should be rendered by
    // renderPlainTextResponse's guidance card, not generic section cards.
    const hasAnyKeyValues = sections.some(section => section.keyValues.length > 0);
    const hasNumberedOnlySections = sections.some(
        section => section.numberedItems.length > 0 && section.keyValues.length === 0,
    );
    if (!hasAnyKeyValues && hasNumberedOnlySections) {
        return null;
    }

    return hasRichStructure ? sections : null;
};

export const getSupportStatusStyles = (status: string) => {
    const normalizedStatus = (status || '').toLowerCase().trim();

    if (!normalizedStatus) {
        return { bg: '#EEF2F6', color: '#475467' };
    }

    if (
        normalizedStatus.includes('resolved') ||
        normalizedStatus.includes('closed') ||
        normalizedStatus.includes('completed')
    ) {
        return { bg: '#DDF3E6', color: '#1F7A4D' };
    }

    if (normalizedStatus.includes('open') || normalizedStatus.includes('new')) {
        return { bg: '#FDE8E8', color: '#B42318' };
    }

    if (
        normalizedStatus.includes('in progress') ||
        normalizedStatus.includes('processing') ||
        normalizedStatus.includes('working')
    ) {
        return { bg: '#E7F0FF', color: '#1D4ED8' };
    }

    if (normalizedStatus.includes('on hold') || normalizedStatus.includes('hold')) {
        return { bg: '#F4F4F5', color: '#3F3F46' };
    }

    if (normalizedStatus.includes('pending')) {
        return { bg: '#FFF3D6', color: '#9B6B00' };
    }

    if (normalizedStatus.includes('cancel') || normalizedStatus.includes('reject')) {
        return { bg: '#FFE4E8', color: '#BE123C' };
    }

    return { bg: '#FDE8E8', color: '#B42318' };
};

export const getContractStatusStyles = (status: string) => {
    const normalizedStatus = (status || '').toLowerCase();
    if (normalizedStatus.includes('closed')) {
        return { bg: '#E5E7EB', color: '#374151' };
    }
    return { bg: '#087262', color: colors.fullWhite };
};

export const getContractTypeBadge = (type: string) => {
    const normalizedType = (type || '').toLowerCase();
    if (normalizedType.includes('lease')) {
        return 'Lease';
    }
    if (normalizedType.includes('loan')) {
        return 'Loan';
    }
    return 'Contract';
};

export const shouldForceGuidanceCardUi = (text: string) => {
    const normalizedText = text.toLowerCase();
    const looksLikeStructuredSupportTicket =
        /ticket\s*number\s*:/i.test(text) &&
        (/request\s*type\s*:/i.test(text) || /subject\s*:/i.test(text) || /created\s*date\s*:/i.test(text));

    // Support-ticket payloads can include "Bank Account" and numbered rows.
    // Those must render as support cards, not guidance cards.
    if (looksLikeStructuredSupportTicket) {
        return false;
    }

    const hasSecurityContext =
        normalizedText.includes('for security reasons') ||
        normalizedText.includes('cannot be displayed here') ||
        normalizedText.includes('please follow these steps') ||
        normalizedText.includes('unable to display');
    const looksLikeUnknownResponse =
        /(i\s+(do not|don't)\s+(have|see|find|access))|\b(unable|cannot|can't|not available|not able)\b|\bi'?m\s+sorry\b/.test(
            normalizedText,
        );

    const hasBankOrDocumentContext =
        /(bank(ing|\s*account|\s*details?)|documents?)/i.test(text) ||
        normalizedText.includes('active and inactive bank accounts');

    const hasSupportContactContext =
        normalizedText.includes('customer service team') ||
        normalizedText.includes('customer support') ||
        normalizedText.includes('submit a support request') ||
        normalizedText.includes('one of three ways') ||
        normalizedText.includes('our team will be happy to help');

    // Digit/roman-numeral runs and separating whitespace are bounded (no real list marker needs
    // more than a handful of characters) so a pathological input can't force unbounded regex work.
    const hasGuidedInstructions =
        /\d{1,6}\.\s{1,20}/.test(text) ||
        /^[a-z]\.\s{1,20}/im.test(text) ||
        /^[ivxlcdm]{1,20}\.\s{1,20}/im.test(text) ||
        normalizedText.includes('navigate to') ||
        normalizedText.includes('support request') ||
        normalizedText.includes('email us') ||
        normalizedText.includes('call us');

    return (
        (hasSecurityContext && hasBankOrDocumentContext) ||
        (hasBankOrDocumentContext && hasGuidedInstructions) ||
        (hasSupportContactContext && hasGuidedInstructions) ||
        ((hasSecurityContext || looksLikeUnknownResponse) && hasGuidedInstructions)
    );
};

// Numbered/lettered/roman-numeral list markers indicate a step-by-step instruction response
// that should render as plain text rather than being claimed by a structured card parser.
export const hasSimpleInstructionSteps = (botText: string): boolean => {
    const hasInstructions = /(?:^|\n)\s*(?:\d{1,6}\.|[a-z]\.|[ivxlcdm]+\.)/im.test(botText);
    const hasKeyValuePairs = /\|\s*[A-Za-z][A-Za-z\s]*:/.test(botText);
    return hasInstructions && !hasKeyValuePairs;
};

// Clarification prompts (e.g. "Please provide the contract ID ... so I can look up the asset
// serial number for you.") are plain prose. They merely mention domain words like "asset serial
// number" / "contract" and must NOT be parsed into asset/contract cards.
export const isClarificationPrompt = (botText: string, isUserMessage: boolean): boolean => {
    const trimmedBotText = (botText || '').trim();
    const isStructuredPayload = trimmedBotText.startsWith('{') || trimmedBotText.startsWith('[');
    return (
        !isUserMessage &&
        !isStructuredPayload &&
        !/[\n|]/.test(trimmedBotText) && // single prose line, no list/pipe structure
        (/\bplease\s+(provide|share|enter|specify|confirm)\b/i.test(trimmedBotText) ||
            /\b(provide|share|enter|specify)\b[^\n]*\b(contract|ticket|account|invoice)\b[^\n]*\b(id|number)\b/i.test(
                trimmedBotText,
            ) ||
            /\bso\s+(?:that\s+)?i\s+can\s+(?:look\s*up|retrieve|find|fetch|pull\s*up|get)\b/i.test(trimmedBotText))
    );
};

export const highlightSupportWord = (value: string): React.ReactNode => {
    if (!value) {
        return value;
    }

    const capitalizeLeadingLetter = (token: string) => {
        const trimmedToken = token.trim();
        if (!trimmedToken) {
            return token;
        }

        const firstLetterIndex = trimmedToken.search(/[a-z]/i);
        if (firstLetterIndex === -1) {
            return trimmedToken;
        }

        return (
            trimmedToken.slice(0, firstLetterIndex) +
            trimmedToken.charAt(firstLetterIndex).toUpperCase() +
            trimmedToken.slice(firstLetterIndex + 1)
        );
    };

    // Match double-quoted phrases, or single-quoted phrases that are genuine
    // quotes (opening quote at start/after whitespace or "(", closing quote at
    // end/before whitespace or closing punctuation). The optional leading
    // boundary char is captured so it can be re-emitted outside the bold span.
    // This avoids treating the apostrophes in contractions like "I'm"/"don't"
    // as quote delimiters, which would otherwise bold and uppercase the letter
    // after the apostrophe. (Lookbehind is avoided for es6 target compatibility.)
    const quotedMatcher = /("[^"]+")|(^|[\s(])('[^']+')(?=$|[\s).,!?;:])/g;
    const output: React.ReactNode[] = [];
    let lastIndex = 0;
    let quotedMatch = quotedMatcher.exec(value);

    while (quotedMatch) {
        if (quotedMatch.index > lastIndex) {
            output.push(value.slice(lastIndex, quotedMatch.index));
        }

        // Group 1 = double-quoted token. Group 2 = leading boundary char for a
        // single-quoted phrase (emitted as plain text), group 3 = the single-quoted token.
        const leadingBoundary = quotedMatch[2] ?? '';
        const token = quotedMatch[1] ?? quotedMatch[3];
        if (leadingBoundary) {
            output.push(leadingBoundary);
        }
        const quote = token[0];
        const inner = token.slice(1, -1);
        output.push(
            <Box key={`quoted-token-${quotedMatch.index}`} component="span" sx={{ fontWeight: 700 }}>
                {`${quote}${capitalizeLeadingLetter(inner)}${quote}`}
            </Box>,
        );

        lastIndex = quotedMatch.index + quotedMatch[0].length;
        quotedMatch = quotedMatcher.exec(value);
    }

    if (lastIndex < value.length) {
        output.push(value.slice(lastIndex));
    }

    if (output.length > 0) {
        return output;
    }

    return value;
};
